import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useConfirm } from "./ConfirmDialog";
import { useToast } from "./Toast";
import SetupCelebration from "./SetupCelebration";
import { requestOnboardingTour, TOUR_QUICK_FILLS } from "./OnboardingTour";
import { API_URL } from './config';
import BisListModal from "./BisListModal";
import AvailabilityGrid from "./components/AvailabilityGrid";
import { useTimezone } from "./lib/TimezoneContext";
import { DEFAULT_RESET_RULE, ResetRule, formatBitRangeLocalLabel, getCurrentResetWeek, getNextResetWeek, localCellToBitIdx } from "./lib/time";
import { resetDayName, resetDayNamesForAccount, ruleForAccount } from "./lib/regionReset";
import { computeReadiness, ReadyState, ReadyTarget } from "./readiness";
import { flashSpotlight, SPOTLIGHT_RADII } from "./spotlight";
import { getWowClassColor as rawClassColor } from "./wowClassColor";
import { useTheme } from "./theme/ThemeProvider";
import { DISPLAY } from "./alignmentUi";

import {
  FieldLabel,
  MODAL_FIELD_CLS,
  CrossfadeImage,
  modalShellStyle,
  getFactionBannerColor,
  optionIconUrl,
  isLightHex,
} from "./modalChrome";


import { shortGameName } from "./lib/gameName";
import { characterLogsUrl, useGameLogs, type GameLogs } from "./lib/gameLogs";
import CharacterJourney from "./CharacterJourney";
// Class and parse colors are authored for a dark background and several vanish
// on the light palette. RosterPage sets this from the active theme on every
// render, before its children render. Identity on dark. See ThemeProvider.
let legible: (hex: string) => string = (hex) => hex;
const getWowClassColor = (label: string | null | undefined) => legible(rawClassColor(label));

// --- CONSTANTS ---

// Class splashes live on the auth API (`express.static` → /class-backgrounds). In dev, Vite is a
// different origin than the API — same-origin `/class-backgrounds/...` 404s unless we prefix API_URL.
const CLASS_BG = `${API_URL}/class-backgrounds`;

const CLASS_ART: Record<string, string> = {
  Warrior:      `${CLASS_BG}/Warrior.png`,
  Paladin:      `${CLASS_BG}/Paladin.png`,
  Hunter:       `${CLASS_BG}/hunter.png`,
  Rogue:        `${CLASS_BG}/rogue.png`,
  Priest:       `${CLASS_BG}/priest.png`,
  "Death Knight": `${CLASS_BG}/deathknight.png`,
  Shaman:       `${CLASS_BG}/shaman.png`,
  Mage:         `${CLASS_BG}/mage.png`,
  Warlock:      `${CLASS_BG}/warlock.png`,
  Monk:         `${CLASS_BG}/monk.png`,
  Druid:        `${CLASS_BG}/druid.png`,
  "Demon Hunter": `${CLASS_BG}/demonhunter.png`,
  Evoker:       `${CLASS_BG}/evoker.png`,
};

function getClassArtUrl(classLabel: string | null | undefined): string | null {
  if (!classLabel || classLabel === "Unknown") return null;
  const norm = classLabel.trim().toLowerCase().replace(/\s+/g, "");
  const key = Object.keys(CLASS_ART).find((k) => k.toLowerCase().replace(/\s+/g, "") === norm);
  return key ? CLASS_ART[key] : null;
}

const TOTAL_AVAIL_SLOTS = 7 * 48;

// The stand-in card the guided tour shows to players with no characters.
const TOUR_EXAMPLE_CHARACTER = { id: "tour-example", name: "Your Character", isSleeping: false };

// --- UTILS ---

function bitstringToBoolArray(bits: string | null | undefined): boolean[] {
  const arr = new Array(TOTAL_AVAIL_SLOTS).fill(false);
  if (!bits) return arr;
  const len = Math.min(bits.length, TOTAL_AVAIL_SLOTS);
  for (let i = 0; i < len; i++) arr[i] = bits[i] === "1";
  return arr;
}

function boolArrayToBitstring(arr: boolean[]): string {
  const out: string[] = [];
  const len = Math.min(arr.length, TOTAL_AVAIL_SLOTS);
  for (let i = 0; i < len; i++) out.push(arr[i] ? "1" : "0");
  for (let i = len; i < TOTAL_AVAIL_SLOTS; i++) out.push("0");
  return out.join("");
}

function stringToColor(str: string): string {
  if (!str) return "#525252";
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const c = (hash & 0x00ffffff).toString(16).toUpperCase();
  return "#" + "00000".substring(0, 6 - c.length) + c;
}

function getParseColor(score: number): string {
  return legible(rawParseColor(score));
}

function rawParseColor(score: number): string {
  if (score === 100) return "#e5cc80";
  if (score >= 99) return "#e268a8";
  if (score >= 95) return "#ff8000";
  if (score >= 75) return "#a335ee";
  if (score >= 50) return "#0070dd";
  if (score >= 25) return "#1eff00";
  return "#9d9d9d";
}

function slugify(str: string): string {
  return str.toLowerCase().trim().replace(/[']/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}

// Reset-week helpers now come from the shared implementation. These were a
// private copy of the "Wednesday 00:00 UTC" rule, which is EU-shaped and a day
// out for US realms. Call with a rule (src/lib/regionReset.ts) for the viewer's
// real reset; the default is EU, matching the previous behaviour exactly.
const getCurrentWednesday = (rule: ResetRule = DEFAULT_RESET_RULE) => getCurrentResetWeek(rule);
const getNextWednesday = (rule: ResetRule = DEFAULT_RESET_RULE) => getNextResetWeek(rule);

function formatWeekRange(weekStart: string): string {
  const wed = new Date(weekStart + 'T00:00:00Z');
  const tue = new Date(wed);
  tue.setDate(tue.getDate() + 6);
  const fmt = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return `${fmt(wed)} - ${fmt(tue)}`;
}

// --- TYPES ---

interface Game { id: string; code: string; name: string; }
interface Option { id: number; key: string; label: string; image_url?: string; metadata: any; }
interface Group { id: number; key: string; label: string; multiSelect: boolean; isRequired: boolean; options: Option[]; }

interface WeekAvailability {
  weekStart: string;
  weekEnd: string;
  slots: string;
  confirmedAt: string | null;
  confirmedVia: string | null;
  copiedFromWeek?: string;
  isAutoCopied?: boolean;
}

interface SquadMember {
  membershipId: string;
  userId: string;
  displayName: string;
  discordId: string;
  joinedAt: string;
  mainCharacter: {
    id: string;
    name: string;
    className: string;
    classColor: string;
    renderUrl: string | null;
  } | null;
  characterCount: number;
}

interface Squad {
  id: string;
  name: string;
  inviteCode: string;
  createdByUserId: string;
  creatorDisplayName: string;
  createdAt: string;
  isOwner: boolean;
  members: SquadMember[];
}

interface PowerBlock {
  start: number;
  end: number;
  avgOverlap: number;
}

interface SquadAvailabilityData {
  targetWeek: string;
  members: { userId: string; displayName: string; slots: string | null; confirmedAt: string | null }[];
  overlap: number[];
  maxOverlap: number;
  membersWithAvailability: number;
  totalMembers: number;
  powerBlocks: PowerBlock[];
}

interface RosterPageProps {
  characters: any[];
  setCharacters: (next: any[] | ((prev: any[]) => any[])) => void;
  accountAvailability: boolean[];
  setAccountAvailability: (next: boolean[]) => void;
  availabilityConfirmation: {
    confirmedWeekStart: string | null;
    confirmedAt: string | null;
    confirmedVia: string | null;
  };
  setAvailabilityConfirmation: (next: { confirmedWeekStart: string | null; confirmedAt: string | null; confirmedVia: string | null }) => void;
  weeklyAvailability: {
    currentWeek: WeekAvailability | null;
    nextWeek: WeekAvailability | null;
  };
  setWeeklyAvailability: (next: { currentWeek: WeekAvailability | null; nextWeek: WeekAvailability | null } | ((prev: { currentWeek: WeekAvailability | null; nextWeek: WeekAvailability | null }) => { currentWeek: WeekAvailability | null; nextWeek: WeekAvailability | null })) => void;
}

/** Roster grid tile: full-bleed class art + bottom identity (no narrow side column). */
export function RosterCharacterTile({
  char,
  classLabel,
  classColor,
  roleOpts,
  realmLabel,
  wclPerf,
  perfScore,
  perfColor,
  heat,
  avatarImage,
  isRefreshing,
  onEdit,
  onRefresh,
  onBis,
  onGuildOpen,
  onDelete,
  joinRequest,
  onWithdrawRequest,
  onDismissRejection,
  gameLabel,
  onJourney,
}: {
  char: any;
  classLabel: string;
  classColor: string;
  roleOpts: Option[];
  realmLabel: string;
  wclPerf: { url?: string; score?: number } | null | undefined;
  perfScore: number | null;
  perfColor: string | null;
  heat: { heat: number; parse_score: number } | null | undefined;
  avatarImage: string | undefined;
  isRefreshing: boolean;
  onEdit: () => void;
  onRefresh: () => void;
  onBis: () => void;
  onGuildOpen: () => void;
  onDelete: () => void;
  joinRequest?: { id: string; status: string; guildName: string; guildTag: string | null } | null;
  onWithdrawRequest?: () => void;
  onDismissRejection?: () => void;
  // Short game name ("Forever"), only passed when the player has characters in more than one game.
  gameLabel?: string | null;
  onJourney?: () => void;
}) {
  const asleep = char.isSleeping === true;
  const classArtUrl = getClassArtUrl(classLabel);
  const [classArtFailed, setClassArtFailed] = useState(false);

  useEffect(() => {
    setClassArtFailed(false);
  }, [classArtUrl, char.id]);

  const showClassImg = Boolean(classArtUrl) && !classArtFailed;

  return (
    <div className={`group/card relative flex flex-col overflow-hidden rounded-2xl border bg-[var(--c-surface)] shadow-[var(--c-shadow-lg)] transition-all duration-300 hover:-translate-y-1 ${asleep ? "border-indigo-400/30 opacity-85" : "border-[var(--c-line)] hover:border-[var(--c-line-strong)]"}`}>
      {/* Hero — main pane shrinks; tool column grows in (guild + actions below stay full width) */}
      {/* The hero is class artwork, so it stays a dark island in both
          palettes (text on art must stay light). */}
      <div className="lfm-dark flex h-60 overflow-hidden">
        <div className="relative min-w-0 flex-1 overflow-hidden transition-[flex] duration-300 ease-out">
          <div
            className="absolute inset-0"
            style={{ background: "radial-gradient(ellipse 100% 70% at 50% 25%, rgba(255,255,255,0.06) 0%, transparent 60%), linear-gradient(180deg, #13141b 0%, #0c0d13 82%)" }}
            aria-hidden
          />
          {showClassImg && classArtUrl ? (
            <img
              src={classArtUrl}
              alt=""
              className={`absolute inset-0 h-full w-full object-cover ${asleep ? "grayscale opacity-40" : "opacity-[0.85]"} [object-position:center_18%] scale-[1.1] transition-[opacity,transform,object-position] duration-300 ease-out will-change-[transform,object-position] group-hover/card:scale-[1.04] group-hover/card:opacity-100 group-hover/card:[object-position:40%_18%] group-focus-within/card:scale-[1.04] group-focus-within/card:[object-position:40%_18%] [@media(hover:none)]:scale-[1.04] [@media(hover:none)]:[object-position:40%_18%]`}
              onError={() => setClassArtFailed(true)}
            />
          ) : null}
          {/* Only the bottom darkens, behind the name; the art itself is not dulled. */}
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#0c0d13] from-5% via-[#0c0d13]/50 via-35% to-transparent to-65%" aria-hidden />

          {asleep && (
            <div className="pointer-events-none absolute inset-0 z-[4] flex items-center justify-end pr-6">
              <span className="lfm-zzz is-asleep text-5xl text-indigo-200/80 drop-shadow-[0_2px_12px_rgba(99,102,241,0.6)]"><i>z</i><i>z</i><i>Z</i></span>
            </div>
          )}
          {/* Parse — corner hint at rest; hidden once the column opens */}
          <div className="pointer-events-none absolute right-4 top-3.5 z-[5] text-right transition-opacity duration-200 group-hover/card:opacity-0 group-focus-within/card:opacity-0 [@media(hover:none)]:opacity-0">
            <div className="text-[8px] font-semibold uppercase tracking-[0.2em] text-[var(--c-ink-4)]">Parse</div>
            <div className="text-2xl font-semibold leading-none tabular-nums" style={{ color: perfColor ?? "var(--c-ink-4)" }}>
              {perfScore ?? "—"}
            </div>
          </div>

          {/* Loot heat — rest hint top-left, fades on hover exactly like the
              Parse hint. The full value reappears in the slide-in tool column.
              Only present when the council awarded this character in the past
              month (gated server-side); colored on the same WCL/parse scale. */}
          {heat ? (
            <div className="pointer-events-none absolute left-4 top-3.5 z-[5] text-left transition-opacity duration-200 group-hover/card:opacity-0 group-focus-within/card:opacity-0 [@media(hover:none)]:opacity-0">
              <div className="text-[8px] font-semibold uppercase tracking-[0.2em] text-[var(--c-ink-4)]">Heat</div>
              <div className="text-2xl font-semibold leading-none tabular-nums" style={{ color: getParseColor(heat.parse_score) }}>
                {Math.round(heat.heat)}
              </div>
            </div>
          ) : null}

          <div className="relative z-[2] flex h-full flex-col items-center justify-center gap-3.5 px-4 transition-[padding] duration-300 ease-out group-hover/card:px-3 group-focus-within/card:px-3">
            <button
              type="button"
              onClick={onJourney}
              title="View journey: raids, companions and loot"
              aria-label={`View ${char.name}'s journey`}
              className="group/journey relative rounded-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--c-accent)]"
            >
              <div className="grid h-[84px] w-[84px] transition-transform group-hover/journey:scale-105 shrink-0 place-items-center overflow-hidden rounded-2xl border border-[var(--c-line)] bg-[var(--c-sunken)] shadow-[var(--c-shadow)]">
              {avatarImage ? (
                <img
                  src={avatarImage}
                  alt=""
                  className="h-full w-full object-cover"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
              ) : (
                <span className="text-3xl font-semibold text-[var(--c-ink-3)]">{classLabel.charAt(0)}</span>
              )}
            </div>
              <span className="pointer-events-none absolute -bottom-2 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-black/70 px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest text-white opacity-0 transition-opacity group-hover/journey:opacity-100">View journey</span>
            </button>

            <div className="w-full min-w-0 text-center">
              {gameLabel && (
                <div className="mb-1.5 inline-block rounded border border-[var(--c-accent-line)] bg-[var(--c-accent-soft)] px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest text-[var(--c-accent-ink)]">
                  {gameLabel}
                </div>
              )}
              <h3 className="text-[1.05rem] font-semibold uppercase leading-tight tracking-[0.09em] text-[var(--c-ink)]">
                <button type="button" onClick={onJourney} title="View journey" className="break-words [overflow-wrap:anywhere] hover:text-[var(--c-accent-ink)] hover:underline decoration-[var(--c-accent-line)] underline-offset-4">{char.name}</button>
              </h3>
              {asleep && (
                <div className="mt-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-indigo-300">
                  Asleep{char.sleptAt ? ` since ${new Date(char.sleptAt).toLocaleDateString(undefined, { day: "numeric", month: "short" })}` : ""}
                </div>
              )}
              <div className="mt-1.5 text-[11px] leading-snug text-[var(--c-ink-3)]">
                <span className="font-medium text-[var(--c-ink-2)]">{classLabel}</span>
                <span className="px-1.5 text-[var(--c-ink-4)]">·</span>
                <span>{realmLabel}</span>
              </div>
            </div>

            <div className="flex max-w-full flex-wrap justify-center gap-1.5">
              {roleOpts.length > 0 ? (
                roleOpts.map((r) => (
                  <span key={r.id} className="rounded-full border border-[var(--c-line)] bg-[var(--c-bg-veil)] px-2.5 py-1 text-[9px] font-semibold uppercase tracking-[0.12em] text-[var(--c-ink-2)]">
                    {r.label}
                  </span>
                ))
              ) : (
                <span className="text-[10px] italic text-[var(--c-ink-4)]">No role set</span>
              )}
            </div>
          </div>
        </div>

        <div
          className="flex w-0 shrink-0 flex-col overflow-hidden opacity-0 backdrop-blur-sm transition-[width,opacity] duration-300 ease-out group-hover/card:w-[90px] group-hover/card:opacity-100 group-focus-within/card:w-[90px] group-focus-within/card:opacity-100 [@media(hover:none)]:w-[90px] [@media(hover:none)]:opacity-100"
          style={{ backgroundColor: "rgba(163,53,238,0.1)", borderLeft: "1px solid rgba(163,53,238,0.3)" }}
        >
          {/* Heat — reveals with the same slide-in as Parse/Sync; only present
              when the council awarded this character in the past month. */}
          {heat ? (
            <div className="flex min-w-[90px] flex-[1.2] flex-col items-center justify-center gap-0.5 border-b border-[#a335ee]/20">
              <span className="text-[8px] font-semibold uppercase tracking-[0.2em] text-[var(--c-ink-3)]">Heat</span>
              <span className="text-[1.7rem] font-semibold leading-none tabular-nums" style={{ color: getParseColor(heat.parse_score) }}>{Math.round(heat.heat)}</span>
            </div>
          ) : null}
          {wclPerf?.url ? (
            <a
              href={wclPerf.url}
              target="_blank"
              rel="noreferrer"
              title="Open Warcraft Logs"
              className="flex min-w-[90px] flex-[1.4] flex-col items-center justify-center gap-0.5 border-b border-[#a335ee]/20 transition-colors hover:bg-[var(--c-sunken)]"
            >
              <span className="text-[8px] font-semibold uppercase tracking-[0.2em] text-[var(--c-ink-3)]">Parse</span>
              <span className="text-[1.9rem] font-semibold leading-none tabular-nums" style={{ color: perfColor ?? "var(--c-ink)" }}>{perfScore ?? "—"}</span>
            </a>
          ) : (
            <div className="flex min-w-[90px] flex-[1.4] flex-col items-center justify-center gap-0.5 border-b border-[#a335ee]/20">
              <span className="text-[8px] font-semibold uppercase tracking-[0.2em] text-[var(--c-ink-3)]">Parse</span>
              <span className="text-[1.9rem] font-semibold leading-none tabular-nums" style={{ color: perfColor ?? "var(--c-ink-4)" }}>{perfScore ?? "—"}</span>
            </div>
          )}
          <button
            type="button"
            data-tour="sync"
            onClick={onRefresh}
            disabled={isRefreshing}
            title="Sync Battle.net portrait, equipped gear, and Warcraft Logs parse"
            aria-label="Sync Battle.net portrait, equipment, and Warcraft Logs parse"
            className="flex min-w-[90px] flex-1 flex-col items-center justify-center gap-1 text-[var(--c-ink-2)] transition-colors hover:bg-[var(--c-sunken)] hover:text-[var(--c-ink)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#a335ee]/50 disabled:opacity-45"
          >
            {isRefreshing ? (
              <svg className="h-5 w-5 animate-spin text-[var(--c-ink)]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden>
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            ) : (
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            )}
            <span className="text-[8px] font-semibold tracking-[0.16em]">Sync</span>
          </button>
        </div>
      </div>

      {/* Guild strip */}
      {char.guildId ? (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onGuildOpen(); }}
          title={char.guildName ? `${char.guildName} — guild` : "Guild"}
          className="flex items-center justify-between gap-2 border-t border-[var(--c-line-soft)] px-4 py-2.5 text-left transition-colors hover:bg-[var(--c-bg-veil)]"
        >
          <span className="flex min-w-0 items-center gap-2">
            {char.guildLogoUrl ? (
              <img src={char.guildLogoUrl} alt="" className="h-5 w-5 shrink-0 rounded-md object-cover ring-1 ring-[var(--c-line)]" />
            ) : (
              <span className="grid h-5 w-5 shrink-0 place-items-center rounded-md bg-[var(--c-sunken)] text-[8px] font-semibold text-[var(--c-ink-3)]">
                {char.guildTag || char.guildName?.charAt(0) || "G"}
              </span>
            )}
            <span className="min-w-0 truncate text-[11px] font-medium text-[var(--c-ink-3)]">{char.guildName}</span>
          </span>
          <span className="shrink-0 text-[8px] font-semibold uppercase tracking-[0.18em] text-[var(--c-ink-4)]">Guild</span>
        </button>
      ) : joinRequest?.status === "pending" ? (
        <div className="flex items-center justify-between gap-2 border-t border-amber-500/15 bg-amber-500/[0.05] px-4 py-2.5">
          <span className="flex min-w-0 items-center gap-2">
            <span className="grid h-5 w-5 shrink-0 place-items-center rounded-md bg-amber-500/15 text-[10px]">⏳</span>
            <span className="min-w-0 truncate text-[11px] font-medium text-amber-700/90 dark:text-amber-300/90">
              Applied to {joinRequest.guildName}
            </span>
          </span>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onWithdrawRequest?.(); }}
            className="shrink-0 rounded-md border border-[var(--c-line)] px-2 py-0.5 text-[9px] font-semibold uppercase tracking-[0.12em] text-[var(--c-ink-3)] transition-colors hover:border-red-400/30 hover:text-red-700 dark:hover:text-red-300"
          >
            Withdraw
          </button>
        </div>
      ) : joinRequest?.status === "rejected" ? (
        <div className="flex items-center justify-between gap-2 border-t border-red-500/15 bg-red-500/[0.05] px-4 py-2.5">
          <span className="flex min-w-0 items-center gap-2">
            <span className="grid h-5 w-5 shrink-0 place-items-center rounded-md bg-red-500/15 text-[10px]">✕</span>
            <span className="min-w-0 truncate text-[11px] font-medium text-red-700/80 dark:text-red-300/80">
              {joinRequest.guildName} declined your application
            </span>
          </span>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onDismissRejection?.(); }}
            title="Dismiss"
            className="shrink-0 rounded-md border border-[var(--c-line)] px-2 py-0.5 text-[9px] font-semibold uppercase tracking-[0.12em] text-[var(--c-ink-3)] transition-colors hover:text-[var(--c-ink)]"
          >
            OK
          </button>
        </div>
      ) : (
        <div className="flex items-center justify-between gap-2 border-t border-[var(--c-line-soft)] px-4 py-2.5">
          <span className="flex min-w-0 items-center gap-2">
            <span className="grid h-5 w-5 shrink-0 place-items-center rounded-md border border-dashed border-[var(--c-line)] text-[8px] text-[var(--c-ink-4)]">
              <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0" />
              </svg>
            </span>
            <span className="text-[11px] font-medium text-[var(--c-ink-4)]">No guild</span>
          </span>
          <span className="shrink-0 text-[8px] font-semibold uppercase tracking-[0.18em] text-[var(--c-ink-4)]">Guild</span>
        </div>
      )}

      {/* Action rail */}
      <div className="flex gap-1.5 px-3.5 py-3">
        <button
          type="button"
          onClick={onEdit}
          className="flex min-h-[2.5rem] flex-1 items-center justify-center gap-1.5 rounded-lg border border-[var(--c-line)] bg-[var(--c-sunken)] text-[10px] font-semibold uppercase tracking-[0.13em] text-[var(--c-ink-2)] transition-colors hover:bg-[var(--c-sunken)] hover:text-[var(--c-ink)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--c-line-strong)]"
        >
          <svg className="h-4 w-4 shrink-0 opacity-90" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z" />
            <path d="M16.862 4.487L19.5 7.125" />
          </svg>
          Edit
        </button>
        <button
          type="button"
          data-tour="bis"
          onClick={onBis}
          className="flex min-h-[2.5rem] flex-1 items-center justify-center gap-1.5 rounded-lg border border-[var(--c-line)] bg-[var(--c-sunken)] text-[10px] font-semibold uppercase tracking-[0.13em] text-[var(--c-ink-2)] transition-colors hover:border-[#a335ee]/30 hover:bg-[#a335ee]/14 hover:text-violet-800 dark:hover:text-violet-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#a335ee]/45"
        >
          <svg className="h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            <path d="M9 12h6M9 16h6" />
          </svg>
          BiS List
        </button>
        <button
          type="button"
          onClick={onDelete}
          aria-label={`Delete ${char.name}`}
          title="Delete character"
          className="grid min-h-[2.5rem] w-[2.5rem] shrink-0 place-items-center rounded-lg border border-[var(--c-line)] bg-[var(--c-sunken)] text-[var(--c-ink-3)] transition-colors hover:border-red-500/30 hover:bg-red-500/[0.12] hover:text-red-700 dark:hover:text-red-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500/45"
        >
          <svg className="h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
          </svg>
        </button>
      </div>
    </div>
  );
}

// --- READINESS CARD ---

/**
 * Account-level checklist tile. Lives as the first cell of the character grid
 * and is deliberately built to the same skeleton as RosterCharacterTile
 * (h-60 body → strip → action rail) so the grid rows stay flush.
 */
interface ReadyRow {
  key: string;
  label: string;
  value: string;
  state: ReadyState;
  /** Shown as a chip instead of `value` when the row is blocking. */
  actionLabel?: string;
  /** Wording used when this row becomes the card's bottom call to action. */
  ctaLabel?: string;
  onClick?: () => void;
  /** Overrides `onClick` for the bottom button, when browsing and fixing differ. */
  ctaOnClick?: () => void;
  /** Nice to have: still listed, but kept out of the score and the call to action. */
  optional?: boolean;
}

function ReadyRowIcon({ state, optional }: { state: ReadyState; optional?: boolean }) {
  if (state === "done") {
    return (
      // Optional wins are still worth a tick, just a quieter one than the
      // ones that actually move the score.
      <svg className={`h-[17px] w-[17px] shrink-0 ${optional ? "text-emerald-700/50 dark:text-emerald-400/50" : "text-emerald-700 dark:text-emerald-400"}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <circle cx="12" cy="12" r="9" />
        <path d="M8.5 12.5l2.5 2.5 4.5-5" />
      </svg>
    );
  }
  if (state === "attention") {
    return (
      <svg className="h-[17px] w-[17px] shrink-0 text-amber-700 dark:text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7.5v5M12 16.2v.3" />
      </svg>
    );
  }
  return (
    <svg className={`h-[17px] w-[17px] shrink-0 ${optional ? "text-[var(--c-ink-4)]" : "text-[var(--c-ink-4)]"}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeDasharray="3 3" aria-hidden>
      <circle cx="12" cy="12" r="9" />
    </svg>
  );
}

export function RosterReadinessTile({
  rows,
  syncLabel,
  cta,
}: {
  rows: ReadyRow[];
  syncLabel: string;
  cta: { label: string; onClick: () => void } | null;
}) {
  // Optional rows are informational only — they never drag the score down.
  const scored = rows.filter((r) => !r.optional);
  const total = scored.length;
  const done = scored.filter((r) => r.state === "done").length;
  const needsYou = total - done;
  const allDone = needsYou === 0;

  // 15.5r circle → 97.39 circumference; offset shrinks as items complete.
  const circumference = 2 * Math.PI * 15.5;
  const offset = circumference * (1 - (total ? done / total : 0));
  const ringColor = allDone ? "#34d399" : "#F59E0B";

  return (
    <div
      data-tour="readiness"
      className={`relative flex flex-col overflow-hidden rounded-2xl border bg-[var(--c-surface)] shadow-[var(--c-shadow-lg)] transition-all duration-300 hover:-translate-y-1 ${
        allDone ? "border-emerald-500/20 hover:border-emerald-500/35" : "border-amber-500/20 hover:border-amber-500/40"
      }`}
    >
      {/* Body — matches the character tile hero height exactly */}
      <div className="flex h-60 flex-col overflow-hidden bg-[var(--c-bg-veil)] px-4 pt-3.5">
        <div className="flex shrink-0 items-center gap-3 border-b border-[var(--c-line-soft)] pb-2.5">
          <div className="relative h-[42px] w-[42px] shrink-0">
            <svg viewBox="0 0 36 36" className="h-[42px] w-[42px] -rotate-90" aria-hidden>
              <circle cx="18" cy="18" r="15.5" fill="none" stroke="rgba(255,255,255,0.09)" strokeWidth="3" />
              <circle
                cx="18"
                cy="18"
                r="15.5"
                fill="none"
                stroke={ringColor}
                strokeWidth="3"
                strokeLinecap="round"
                strokeDasharray={circumference}
                strokeDashoffset={offset}
                className="transition-[stroke-dashoffset] duration-500 ease-out"
              />
            </svg>
            <div className="absolute inset-0 grid place-items-center text-[12px] font-semibold tabular-nums" style={{ color: ringColor }}>
              {done}/{total}
            </div>
          </div>
          <div className="min-w-0">
            <h3 className="text-[0.95rem] font-semibold uppercase leading-tight tracking-[0.09em] text-[var(--c-ink)]">Raid Ready</h3>
            <div className="mt-0.5 text-[11px] leading-snug text-[var(--c-ink-3)]">
              {allDone ? "You're all set" : `${needsYou} thing${needsYou === 1 ? "" : "s"} need${needsYou === 1 ? "s" : ""} you`}
            </div>
          </div>
        </div>

        {/* Rows split the remaining height evenly so 6 items always fit */}
        <div className="flex min-h-0 flex-1 flex-col">
          {rows.map((row, i) => {
            const blocking = row.state === "attention";
            // The tinted band is its own separator — a white hairline running
            // into or across it just looks like a crack, so drop the divider
            // wherever a row touches the band.
            const touchesBand = blocking || rows[i + 1]?.state === "attention";
            // A labelled rule introduces the optional block, so the row above
            // it drops its own hairline rather than doubling up.
            const startsOptional = !!row.optional && !rows[i - 1]?.optional;
            const precedesOptional = !row.optional && !!rows[i + 1]?.optional;
            const RowTag = row.onClick ? "button" : "div";
            return (
              <React.Fragment key={row.key}>
                {startsOptional && (
                  <div className="flex shrink-0 items-center gap-2 py-0.5" aria-hidden>
                    <span className="text-[8px] font-semibold uppercase tracking-[0.18em] text-[var(--c-ink-4)]">Optional</span>
                    <span className="h-px flex-1 bg-[var(--c-sunken)]" />
                  </div>
                )}
                <RowTag
                  {...(row.onClick ? { type: "button" as const, onClick: row.onClick } : {})}
                  className={`flex min-h-0 flex-1 items-center gap-2.5 text-left transition-colors ${
                    i < rows.length - 1 && !touchesBand && !precedesOptional ? "border-b border-[var(--c-line-soft)]" : ""
                  } ${blocking ? "-mx-4 bg-amber-500/[0.07] px-4" : ""} ${
                    row.onClick
                      ? `cursor-pointer focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset ${
                          blocking ? "hover:bg-amber-500/[0.12] focus-visible:ring-amber-500/40" : "hover:bg-[var(--c-sunken)] focus-visible:ring-[var(--c-line-strong)]"
                        }`
                      : ""
                  }`}
                >
                  <ReadyRowIcon state={row.state} optional={row.optional} />
                  <span
                    className={`min-w-0 flex-1 truncate text-[12.5px] ${
                      blocking ? "text-amber-800 dark:text-amber-200" : row.optional ? "text-[var(--c-ink-3)]" : row.state === "done" ? "text-[var(--c-ink-2)]" : "text-[var(--c-ink-3)]"
                    }`}
                  >
                    {row.label}
                  </span>
                  {blocking && row.actionLabel ? (
                    <span className="shrink-0 rounded-md border border-amber-500/40 px-2 py-[1px] text-[9px] font-semibold uppercase tracking-[0.1em] text-amber-700 dark:text-amber-400">
                      {row.actionLabel}
                    </span>
                  ) : (
                    <span className={`shrink-0 text-[11px] ${row.optional ? "text-[var(--c-ink-4)]" : "text-[var(--c-ink-3)]"}`}>{row.value}</span>
                  )}
                </RowTag>
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Strip — mirrors the character tile's guild strip */}
      <div className="flex items-center justify-between gap-2 border-t border-[var(--c-line-soft)] px-4 py-2.5">
        <span className="flex min-w-0 items-center gap-2 text-[11px] font-medium text-[var(--c-ink-3)]">
          <svg className="h-3.5 w-3.5 shrink-0 text-[var(--c-ink-3)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          <span className="truncate">{syncLabel}</span>
        </span>
        <span className="shrink-0 text-[8px] font-semibold uppercase tracking-[0.18em] text-[var(--c-ink-4)]">Account</span>
      </div>

      {/* Action rail */}
      <div className="flex gap-1.5 px-3.5 py-3">
        {cta ? (
          <button
            type="button"
            onClick={cta.onClick}
            className="flex min-h-[2.5rem] flex-1 items-center justify-center gap-1.5 rounded-lg border border-amber-500/35 bg-amber-500/[0.14] px-2 text-[10px] font-semibold uppercase tracking-[0.13em] text-amber-800 dark:text-amber-200 transition-colors hover:border-amber-500/55 hover:bg-amber-500/[0.22] hover:text-amber-800 dark:hover:text-amber-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500/45"
          >
            <svg className="h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M13 7l5 5-5 5M6 12h12" />
            </svg>
            <span className="truncate">{cta.label}</span>
          </button>
        ) : (
          <div className="flex min-h-[2.5rem] flex-1 items-center justify-center gap-1.5 rounded-lg border border-emerald-500/25 bg-emerald-500/[0.08] text-[10px] font-semibold uppercase tracking-[0.13em] text-emerald-700/85 dark:text-emerald-300/85">
            <svg className="h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M5 13l4 4L19 7" />
            </svg>
            Nothing to do
          </div>
        )}
      </div>
    </div>
  );
}

export type CharacterFormValues = {
  gameId: string;
  name: string;
  options: Record<number, number | number[]>;
  wclUrl: string | null;
  // Create only: also add the new character to this guild (one the player is
  // already in, whose game/region/realm/faction match). See /me/guild-games.
  addToGuildId?: string | null;
};

export type MyGuildGame = {
  guildId: string; guildName: string; gameId: string;
  serverName: string | null; faction: string | null; region: string | null;
};

function buildWclUrl(
  name: string,
  selectedOptions: Record<number, number | number[]>,
  groups: Group[],
  logs: GameLogs | null | undefined
): string {
  if (!name.trim() || !logs) return "";
  const realmGroup = groups.find((g) => g.key === "realm");
  const regionGroup = groups.find((g) => g.key === "region");
  let region = "eu";
  let realmSlug = "";
  if (realmGroup) {
    const val = selectedOptions[realmGroup.id];
    const opt = realmGroup.options.find((o) => o.id === val);
    if (opt) realmSlug = opt.key || slugify(opt.label);
  }
  if (regionGroup) {
    const val = selectedOptions[regionGroup.id];
    const opt = regionGroup.options.find((o) => o.id === val);
    if (opt) region = opt.label.toLowerCase();
  }
  return characterLogsUrl(logs, { region, realmSlug, name });
}

function optionsFromCharacter(char: any, groups: Group[]): Record<number, number | number[]> {
  const optsMap: Record<number, number | number[]> = {};
  if (!Array.isArray(char?.options)) return optsMap;
  for (const entry of char.options) {
    const gid = entry.group_id;
    const optId = entry.option_id;
    const groupDef = groups.find((g) => g.id === gid);
    if (groupDef?.multiSelect) {
      const existing = (optsMap[gid] as number[]) || [];
      optsMap[gid] = [...existing, optId];
    } else {
      optsMap[gid] = optId;
    }
  }
  return optsMap;
}

function CharacterFormModal({
  mode,
  games,
  fieldsByGame,
  character,
  onClose,
  onSubmit,
  myGuildGames = [],
  onToggleSleep,
}: {
  mode: "create" | "edit";
  games: Game[];
  fieldsByGame: Record<string, Group[]>;
  character: any | null;
  onClose: () => void;
  onSubmit: (values: CharacterFormValues) => Promise<string | null>;
  myGuildGames?: MyGuildGame[];
  // Edit only: put the character to sleep / wake it (takes effect at once).
  onToggleSleep?: () => void;
}) {
  const initialGameId = character
    ? character.gameId ?? character.game_id
    : games[0]?.id || "";
  const [gameId, setGameId] = useState(initialGameId);
  const [selectedOptions, setSelectedOptions] = useState<Record<number, number | number[]>>(() =>
    character ? optionsFromCharacter(character, fieldsByGame[initialGameId] || []) : {}
  );
  const [wclLink, setWclLink] = useState(character?.metadata?.wcl_url || "");
  const [formError, setFormError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const nameRef = useRef<HTMLInputElement>(null);
  const [headerName, setHeaderName] = useState(character?.name || "");
  const nameDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const groups = fieldsByGame[gameId] || [];
  const gameLogs = useGameLogs(gameId);

  // Guilds the player is already in whose game + region + realm + faction match
  // what is picked here: the new character can go straight into one of them.
  const pickedLabel = (key: string): string | null => {
    const g = groups.find((x) => x.key === key);
    const v = g ? selectedOptions[g.id] : undefined;
    return typeof v === "number" ? g!.options.find((o) => o.id === v)?.label ?? null : null;
  };
  const same = (a: string | null | undefined, b: string | null | undefined) =>
    (a || "").trim().toLowerCase() === (b || "").trim().toLowerCase();
  const guildMatches = mode === "create"
    ? myGuildGames.filter((gg) =>
        gg.gameId === gameId &&
        !!pickedLabel("realm") && same(pickedLabel("realm"), gg.serverName) &&
        (!gg.faction || same(gg.faction, "neutral") || same(pickedLabel("faction"), gg.faction)) &&
        (!gg.region || same(pickedLabel("region"), gg.region)))
    : [];
  const [addToGuildId, setAddToGuildId] = useState<string>("");
  const [addToGuildOn, setAddToGuildOn] = useState(true);
  const matchKey = guildMatches.map((m) => m.guildId).join(",");
  useEffect(() => {
    // Keep the choice valid as the picks change; default to the first match.
    if (!guildMatches.some((m) => m.guildId === addToGuildId)) setAddToGuildId(guildMatches[0]?.guildId || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchKey]);
  const classGroup = groups.find((g) => g.key === "class");
  const specGroup = groups.find((g) => g.key === "spec");
  const factionGroup = groups.find((g) => g.key === "faction");
  const regionGroup = groups.find((g) => g.key === "region");
  const realmGroup = groups.find((g) => g.key === "realm");
  const raidGroup = groups.find((g) => g.key === "raid_interest" || g.key === "raid_preferences");

  const [readyToRaid, setReadyToRaid] = useState(() => {
    if (!raidGroup) return false;
    const val = character ? optionsFromCharacter(character, fieldsByGame[initialGameId] || [])[raidGroup.id] : undefined;
    return Array.isArray(val) ? val.length > 0 : !!val;
  });

  const selectedClassId = classGroup ? selectedOptions[classGroup.id] : undefined;
  const selectedClass =
    classGroup && typeof selectedClassId === "number"
      ? classGroup.options.find((o) => o.id === selectedClassId) || null
      : null;
  const selectedClassKey = selectedClass?.key || null;
  const classLabel = selectedClass?.label || null;
  const accent = getWowClassColor(classLabel);
  const splashUrl = getClassArtUrl(classLabel);
  const classIconUrl = optionIconUrl(selectedClass, classLabel);
  const hasClass = Boolean(classLabel);
  const shellAccent = hasClass ? accent : "#27272a";
  const RAID_GREEN = "#34d399";
  const RAID_GREEN_TEXT = "#a7f3d0";

  const selectedSpecLabels = (() => {
    if (!specGroup) return [];
    const val = selectedOptions[specGroup.id];
    const ids = Array.isArray(val) ? val : val ? [val] : [];
    return specGroup.options.filter((o) => ids.includes(o.id)).map((o) => o.label);
  })();
  const selectedRealmLabel = (() => {
    if (!realmGroup) return "";
    const val = selectedOptions[realmGroup.id];
    return realmGroup.options.find((o) => o.id === val)?.label || "";
  })();

  useEffect(() => {
    if (mode === "edit") return;
    const url = buildWclUrl(headerName, selectedOptions, groups, gameLogs);
    if (url) setWclLink(url);
  }, [headerName, selectedOptions, groups, gameLogs, mode]);

  const onNameInput = () => {
    if (nameDebounceRef.current) clearTimeout(nameDebounceRef.current);
    nameDebounceRef.current = setTimeout(() => {
      setHeaderName(nameRef.current?.value || "");
    }, 300);
  };

  const setGroupValue = (group: Group, val: number | number[]) => {
    setSelectedOptions((prev) => {
      const next = { ...prev, [group.id]: val };
      if (group.key === "class") {
        const roleG = groups.find((g) => g.key === "role");
        const specG = groups.find((g) => g.key === "spec");
        if (roleG) delete next[roleG.id];
        if (specG) delete next[specG.id];
      }
      if (group.key === "region") {
        const realmG = groups.find((g) => g.key === "realm");
        if (realmG) delete next[realmG.id];
      }
      return next;
    });
  };

  useEffect(() => {
    return () => {
      if (nameDebounceRef.current) clearTimeout(nameDebounceRef.current);
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    const name = (nameRef.current?.value || "").trim();
    if (!name) {
      setFormError("Character name is required");
      return;
    }
    if (name.length < 2) {
      setFormError("Character name must be at least 2 characters");
      return;
    }
    if (name.length > 24) {
      setFormError("Character name must be 24 characters or less");
      return;
    }
    const requiredKeys = ["class", "spec", "realm", "faction", "region"];
    const missingFields: string[] = [];
    for (const group of groups) {
      if (!requiredKeys.includes(group.key)) continue;
      const optionValue = selectedOptions[group.id];
      const hasValue = Array.isArray(optionValue)
        ? optionValue.length > 0
        : optionValue !== undefined && optionValue !== null && Number(optionValue) > 0;
      if (!hasValue) missingFields.push(group.label || group.key);
    }
    if (missingFields.length > 0) {
      setFormError(`Please select: ${missingFields.join(", ")}`);
      return;
    }
    setIsSubmitting(true);
    const payloadOptions: Record<number, number | number[]> = {};
    for (const [gidStr, val] of Object.entries(selectedOptions)) {
      const gid = Number(gidStr);
      if (Array.isArray(val)) {
        if (val.length > 0) payloadOptions[gid] = val;
      } else if (val > 0) payloadOptions[gid] = val;
    }
    const wclUrl =
      mode === "edit"
        ? wclLink.trim() || null
        : buildWclUrl(name, selectedOptions, groups, gameLogs).trim() || wclLink.trim() || null;
    try {
      const err = await onSubmit({
        gameId, name, options: payloadOptions, wclUrl,
        addToGuildId: addToGuildOn && guildMatches.some((m) => m.guildId === addToGuildId) ? addToGuildId : null,
      });
      if (err) setFormError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const sublineParts = [classLabel, selectedSpecLabels[0], selectedRealmLabel].filter(Boolean);
  const titleText = headerName.trim() || (mode === "create" ? "Add Character" : "Edit Character");
  const ctaText = isSubmitting ? "Saving..." : mode === "create" ? "Add to Roster" : "Save Changes";

  const renderRemainingGroups = (groupsToRender: Group[]) => {
    return groupsToRender.map((group) => {
      if (!group.options.length) return null;
      const currentVal = selectedOptions[group.id];

      if (group.options.every((o) => o.metadata?.ui_component === "toggle")) {
        return (
          <div key={group.id} className="space-y-2 pt-2">
            <FieldLabel>{group.label}</FieldLabel>
            <div className="grid grid-cols-1 gap-2">
              {group.options.map((opt) => {
                const isChecked = Array.isArray(currentVal)
                  ? currentVal.includes(opt.id)
                  : currentVal === opt.id;
                let desc = opt.metadata?.description || "";
                if (!desc && opt.key === "willing_raid_lead") desc = "Raid Leader";
                if (!desc && opt.key === "willing_loot_master") desc = "Master Looter";
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => {
                      let next = Array.isArray(currentVal)
                        ? [...currentVal]
                        : currentVal
                          ? [currentVal as number]
                          : [];
                      if (isChecked) next = next.filter((id) => id !== opt.id);
                      else next.push(opt.id);
                      setSelectedOptions((prev) => ({ ...prev, [group.id]: next }));
                    }}
                    className="char-modal-tone flex items-center justify-between p-3 rounded-xl border text-left"
                    style={
                      isChecked
                        ? { backgroundColor: `${accent}22`, borderColor: `${accent}80` }
                        : { backgroundColor: "rgba(0,0,0,0.4)", borderColor: "rgba(255,255,255,0.1)" }
                    }
                  >
                    <div>
                      <span className={`text-xs font-bold ${isChecked ? "text-[var(--c-ink)]" : "text-[var(--c-ink-2)]"}`}>
                        {opt.label}
                      </span>
                      {desc && <div className="text-[10px] text-[var(--c-ink-3)]">{desc}</div>}
                    </div>
                    <div
                      className="char-modal-tone w-8 h-4 rounded-full relative"
                      style={{ backgroundColor: isChecked ? accent : "rgba(255,255,255,0.1)" }}
                    >
                      <div
                        className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-all ${isChecked ? "left-[18px]" : "left-0.5"}`}
                      />
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        );
      }

      if (group.key === "role") return null;

      if (group.multiSelect) {
        if (group.key === "raid_interest" || group.key === "raid_preferences") {
          return (
            <div key={group.id} className="space-y-3 pt-3 border-t border-[var(--c-line)] mt-1">
              <label
                className="relative flex items-center gap-3 cursor-pointer group/rtr"
                onClick={(e) => {
                  e.preventDefault();
                  if (readyToRaid) {
                    setReadyToRaid(false);
                    setSelectedOptions((prev) => {
                      const n = { ...prev };
                      delete n[group.id];
                      return n;
                    });
                  } else {
                    setReadyToRaid(true);
                    // Tick every raid on enable. Raid-ready with nothing selected
                    // makes a character invisible to ARC.
                    setSelectedOptions((prev) => {
                      const cur = prev[group.id];
                      const hasAny = Array.isArray(cur) ? cur.length > 0 : !!cur;
                      return hasAny ? prev : { ...prev, [group.id]: group.options.map((o) => o.id) };
                    });
                  }
                }}
              >
                <div
                  className="char-modal-tone w-5 h-5 rounded border flex items-center justify-center flex-shrink-0"
                  style={
                    readyToRaid
                      ? { backgroundColor: RAID_GREEN, borderColor: RAID_GREEN }
                      : { backgroundColor: "transparent", borderColor: "rgba(255,255,255,0.28)" }
                  }
                >
                  {readyToRaid && (
                    <svg className="w-3 h-3" fill="none" stroke="#052e16" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
                <span
                  className={
                    readyToRaid
                      ? "ready-raid-title"
                      : "char-modal-tone text-xs font-bold uppercase tracking-widest text-[var(--c-ink)]"
                  }
                >
                  Ready to Raid
                </span>
                <div className="absolute left-8 top-full mt-2 px-3 py-2 bg-[var(--c-surface)] shadow-[var(--c-shadow-lg)] border border-[var(--c-line-strong)] rounded-lg text-xs text-[var(--c-ink)] w-64 opacity-0 pointer-events-none group-hover/rtr:opacity-100 z-50 shadow-xl normal-case tracking-normal font-normal leading-relaxed">
                  Mark this character as raid-ready and select which raids you're interested in.
                </div>
              </label>
              {readyToRaid && (
                <div className="flex flex-wrap gap-2 pl-8">
                  {group.options.map((opt) => {
                    const isSelected = Array.isArray(currentVal)
                      ? currentVal.includes(opt.id)
                      : currentVal === opt.id;
                    return (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => {
                          let next = Array.isArray(currentVal)
                            ? [...currentVal]
                            : currentVal
                              ? [currentVal as number]
                              : [];
                          if (isSelected) next = next.filter((id) => id !== opt.id);
                          else next.push(opt.id);
                          setSelectedOptions((prev) => ({ ...prev, [group.id]: next }));
                        }}
                        className="char-modal-tone px-3 py-1.5 rounded-full text-xs font-medium border"
                        style={
                          isSelected
                            ? {
                                backgroundColor: "rgba(16, 185, 129, 0.18)",
                                borderColor: RAID_GREEN,
                                color: RAID_GREEN_TEXT,
                              }
                            : {
                                backgroundColor: "rgba(0,0,0,0.4)",
                                borderColor: "rgba(255,255,255,0.1)",
                                color: "rgba(255,255,255,0.45)",
                              }
                        }
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        }

        const visibleOptions = group.options.filter((opt) => {
          if (group.key === "role" || group.key === "spec") {
            const allowed = opt.metadata?.allowed_class_keys;
            if (!selectedClassKey) return false;
            if (allowed && allowed.length > 0) return allowed.includes(selectedClassKey);
            return false;
          }
          return true;
        });
        if (visibleOptions.length === 0) return null;

        return (
          <div key={group.id} className="space-y-2">
            <FieldLabel>{group.label}</FieldLabel>
            <div className="flex flex-wrap gap-2">
              {visibleOptions.map((opt) => {
                const isSelected = Array.isArray(currentVal)
                  ? currentVal.includes(opt.id)
                  : currentVal === opt.id;
                const color = opt.metadata?.role_color || accent;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => {
                      let next = Array.isArray(currentVal)
                        ? [...currentVal]
                        : currentVal
                          ? [currentVal as number]
                          : [];
                      if (isSelected) next = next.filter((id) => id !== opt.id);
                      else next.push(opt.id);
                      setSelectedOptions((prev) => ({ ...prev, [group.id]: next }));
                    }}
                    className="char-modal-tone px-3 py-1.5 rounded-full text-xs font-medium border"
                    style={
                      isSelected
                        ? { borderColor: color, backgroundColor: `${color}33`, color: "#fff" }
                        : { backgroundColor: "rgba(0,0,0,0.4)", borderColor: "rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.6)" }
                    }
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
        );
      }

      let visibleOptions = group.options;
      // Classes a faction cannot play in this game (e.g. WoW Forever has no
      // Blood Elf or Draenei, so Paladin is Alliance-only, Shaman Horde-only).
      if (group.key === "class") {
        const factionGroup = groups.find((g) => g.key === "faction");
        const fVal = factionGroup ? selectedOptions[factionGroup.id] : undefined;
        const factionKey = typeof fVal === "number" ? factionGroup!.options.find((o) => o.id === fVal)?.key || null : null;
        if (factionKey) {
          visibleOptions = group.options.filter((opt) => {
            const allowed = opt.metadata?.allowed_faction_keys;
            return !Array.isArray(allowed) || allowed.length === 0 || allowed.includes(factionKey);
          });
        }
      }
      if (group.key === "realm") {
        let selectedRegionKey: string | null = null;
        if (regionGroup) {
          const val = selectedOptions[regionGroup.id];
          if (typeof val === "number")
            selectedRegionKey = regionGroup.options.find((o) => o.id === val)?.key || null;
        }
        if (!selectedRegionKey) visibleOptions = [];
        else {
          visibleOptions = group.options.filter((opt) => {
            const allowed = opt.metadata?.allowed_region_keys;
            return !allowed || allowed.includes(selectedRegionKey);
          });
        }
      }

      return (
        <div key={group.id} className="space-y-1.5">
          <FieldLabel>{group.label}</FieldLabel>
          <div className="relative">
            <select
              value={typeof currentVal === "number" ? currentVal : ""}
              onChange={(e) => setGroupValue(group, Number(e.target.value))}
              className={MODAL_FIELD_CLS}
              disabled={group.key === "realm" && visibleOptions.length === 0}
            >
              <option value="">
                {group.key === "realm" && visibleOptions.length === 0 ? "Select Region first..." : "Select..."}
              </option>
              {visibleOptions.map((opt) => (
                <option key={opt.id} value={opt.id}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      );
    });
  };

  const specOptions = specGroup
    ? specGroup.options.filter((opt) => {
        const allowed = opt.metadata?.allowed_class_keys;
        if (!selectedClassKey) return false;
        if (allowed && allowed.length > 0) return allowed.includes(selectedClassKey);
        return false;
      })
    : [];

  const identitySelects = groups.filter((g) => g.key === "region" || g.key === "realm");
  const restGroups = groups.filter(
    (g) =>
      ![
        "region",
        "realm",
        "faction",
        "name",
        "class",
        "spec",
        "role",
        "raid_interest",
        "raid_preferences",
      ].includes(g.key)
  );
  const raidGroups = groups.filter((g) => g.key === "raid_interest" || g.key === "raid_preferences");

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm animate-in fade-in" onClick={onClose} />
      <div
        className="char-modal-shell relative w-full max-w-3xl bg-[var(--c-surface)] border rounded-xl flex flex-col max-h-[90vh] overflow-hidden animate-in zoom-in-95 duration-200"
        style={modalShellStyle(shellAccent)}
      >
        <div className="lfm-dark relative h-48 shrink-0 overflow-hidden">
          <div className="char-modal-splash-idle absolute inset-0 z-0" />
          <CrossfadeImage
            src={splashUrl}
            className="absolute inset-0 z-0 h-full w-full"
            imgClassName="absolute inset-0 h-full w-full object-cover [object-position:center_18%] scale-110"
          />
          <div className="pointer-events-none absolute inset-0 z-[1] bg-gradient-to-t from-[#09090b] via-[#09090b]/35 to-black/10" />
          <div className="relative z-[2] h-full flex items-end gap-3 p-5">
            <div
              className="char-modal-tone relative w-14 h-14 rounded-xl border overflow-hidden shrink-0 bg-[var(--c-surface)]"
              style={
                hasClass
                  ? { borderColor: `${accent}66`, boxShadow: `0 0 18px ${accent}44` }
                  : { borderColor: "rgba(255,255,255,0.1)", boxShadow: "none" }
              }
            >
              {classIconUrl ? (
                <CrossfadeImage src={classIconUrl} className="absolute inset-0 h-full w-full" />
              ) : null}
            </div>
            <div className="min-w-0 flex-1 pb-0.5">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[var(--c-ink-3)]">
                {mode === "create" ? "New Character" : "Edit Character"}
              </div>
              <h3
                className="char-modal-tone text-xl font-bold truncate leading-tight"
                style={{
                  color: hasClass ? accent : "rgba(255,255,255,0.92)",
                  textShadow: hasClass ? "0 1px 10px rgba(0,0,0,0.85)" : "none",
                }}
              >
                {titleText}
              </h3>
              <div
                className="text-xs truncate mt-0.5"
                style={{
                  color: hasClass ? "rgba(255,255,255,0.7)" : "rgba(255,255,255,0.4)",
                  textShadow: hasClass ? "0 1px 8px rgba(0,0,0,0.8)" : "none",
                }}
              >
                {sublineParts.length ? sublineParts.join(" · ") : "Pick a class to preview the sheet."}
              </div>
            </div>
            <button onClick={onClose} className="text-[var(--c-ink-3)] hover:text-[var(--c-ink)] self-start mt-1">
              ✕
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6 scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent">
          {formError && (
            <div className="mb-4 p-3 rounded-lg bg-red-500/20 border border-red-500/30 text-red-800 dark:text-red-200 text-sm">
              {formError}
            </div>
          )}

          <form id="charForm" onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-5">
              <div className="space-y-1.5">
                <FieldLabel>Game</FieldLabel>
                <select
                  value={gameId}
                  onChange={(e) => {
                    setGameId(e.target.value);
                    setSelectedOptions({});
                    setReadyToRaid(false);
                    setFormError(null);
                  }}
                  className={MODAL_FIELD_CLS}
                >
                  {games.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-1.5">
                <FieldLabel>Character Name</FieldLabel>
                <input
                  ref={nameRef}
                  defaultValue={character?.name || ""}
                  onInput={onNameInput}
                  maxLength={24}
                  minLength={2}
                  className={`${MODAL_FIELD_CLS} text-base font-semibold`}
                  placeholder="Name"
                  required
                />
              </div>

              {renderRemainingGroups(identitySelects)}

              {factionGroup && factionGroup.options.length > 0 && (
                <div className="space-y-1.5">
                  <FieldLabel>{factionGroup.label}</FieldLabel>
                  <div className="grid grid-cols-3 gap-2">
                    {factionGroup.options.map((opt) => {
                      const color = getFactionBannerColor(opt.label);
                      const selected = selectedOptions[factionGroup.id] === opt.id;
                      return (
                        <button
                          key={opt.id}
                          type="button"
                          onClick={() => setGroupValue(factionGroup, opt.id)}
                          className="char-modal-tone rounded-xl py-3 text-[11px] font-bold uppercase tracking-widest border"
                          style={
                            selected
                              ? {
                                  borderColor: color,
                                  backgroundColor: `${color}22`,
                                  color,
                                  boxShadow: `0 0 18px ${color}33`,
                                }
                              : { borderColor: "rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.5)" }
                          }
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="pt-2">
                {wclLink ? (
                  <a
                    href={wclLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="char-modal-tone flex items-center gap-3 p-3 rounded-xl border"
                    style={{
                      borderColor: "rgba(244, 140, 64, 0.45)",
                      backgroundColor: "rgba(244, 140, 64, 0.1)",
                      boxShadow: "0 0 18px rgba(244, 140, 64, 0.12)",
                    }}
                  >
                    <div
                      className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 border"
                      style={{ backgroundColor: "rgba(244, 140, 64, 0.2)", borderColor: "rgba(244, 140, 64, 0.4)" }}
                    >
                      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="#f48c40" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4 19V9m5 10V5m5 14v-8m5 8V3" />
                      </svg>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: "#f48c40" }}>
                        Warcraft Logs
                      </div>
                      <div className="text-[11px] font-mono text-[var(--c-ink-2)] truncate mt-0.5">
                        {wclLink.replace(/^https?:\/\//, "")}
                      </div>
                    </div>
                    <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="#f48c40" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                    </svg>
                  </a>
                ) : (
                  <div className="flex items-center gap-3 p-3 rounded-xl border border-[var(--c-line)] bg-[var(--c-bg-veil)]">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 border border-[var(--c-line)] bg-[var(--c-surface)]">
                      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4 19V9m5 10V5m5 14v-8m5 8V3" />
                      </svg>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--c-ink-3)]">Warcraft Logs</div>
                      <div className="text-[11px] text-[var(--c-ink-4)] truncate mt-0.5">
                        Fills in once name and realm are set
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-5">
              {classGroup && (
                <div className="space-y-2">
                  <FieldLabel>{classGroup.label}</FieldLabel>
                  <div className="grid grid-cols-4 sm:grid-cols-7 gap-2">
                    {classGroup.options.map((opt) => {
                      const color = getWowClassColor(opt.label);
                      const selected = selectedOptions[classGroup.id] === opt.id;
                      const icon = optionIconUrl(opt, opt.label);
                      return (
                        <button
                          key={opt.id}
                          type="button"
                          title={opt.label}
                          onClick={() => setGroupValue(classGroup, opt.id)}
                          className="char-modal-tone relative aspect-square rounded-xl border overflow-hidden bg-[var(--c-surface)]"
                          style={
                            selected
                              ? { borderColor: color, boxShadow: `0 0 16px ${color}66` }
                              : { borderColor: "rgba(255,255,255,0.1)" }
                          }
                        >
                          {icon ? (
                            <img src={icon} alt="" className="absolute inset-0 h-full w-full object-cover" />
                          ) : (
                            <span className="absolute inset-0 flex items-center justify-center text-sm font-semibold" style={{ color }}>
                              {opt.label.charAt(0)}
                            </span>
                          )}
                          <span className="absolute inset-x-0 bottom-0 pt-4 pb-1 px-0.5 bg-gradient-to-t from-black/90 via-black/55 to-transparent">
                            <span
                              className="block text-[10px] font-medium leading-tight text-center"
                              style={{
                                color: selected ? color : "rgba(255,255,255,0.82)",
                                textShadow: "0 1px 4px rgba(0,0,0,0.9)",
                              }}
                            >
                              {opt.label}
                            </span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {specGroup && (
                <div className="space-y-2">
                  <FieldLabel>{specGroup.label}</FieldLabel>
                  {!selectedClassKey ? (
                    <div className="text-xs text-[var(--c-ink-4)]">Select a class to see specs.</div>
                  ) : specOptions.length === 0 ? (
                    <div className="text-xs text-[var(--c-ink-4)]">No specs available for this class.</div>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {specOptions.map((opt) => {
                        const currentVal = selectedOptions[specGroup.id];
                        const isSelected = Array.isArray(currentVal)
                          ? currentVal.includes(opt.id)
                          : currentVal === opt.id;
                        const icon = optionIconUrl(opt, classLabel);
                        return (
                          <button
                            key={opt.id}
                            type="button"
                            onClick={() => {
                              let next = Array.isArray(currentVal)
                                ? [...currentVal]
                                : currentVal
                                  ? [currentVal as number]
                                  : [];
                              if (specGroup.multiSelect) {
                                if (isSelected) next = next.filter((id) => id !== opt.id);
                                else next.push(opt.id);
                                setGroupValue(specGroup, next);
                              } else {
                                setGroupValue(specGroup, opt.id);
                              }
                            }}
                            className="char-modal-tone flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-medium border"
                            style={
                              isSelected
                                ? { borderColor: accent, backgroundColor: `${accent}33`, color: "#fff" }
                                : { backgroundColor: "rgba(0,0,0,0.4)", borderColor: "rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.6)" }
                            }
                          >
                            {icon && <img src={icon} alt="" className="w-4 h-4 rounded" />}
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {renderRemainingGroups(raidGroups)}
              {renderRemainingGroups(restGroups)}
            </div>
          </form>
        </div>

        <div className="p-5 border-t border-[var(--c-line)] bg-[var(--c-bg-veil)]">
          {mode === "edit" && character && onToggleSleep && (
            <button
              type="button"
              onClick={onToggleSleep}
              aria-pressed={!!character.isSleeping}
              className={`mb-3 flex w-full items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition-colors ${
                character.isSleeping
                  ? "border-indigo-400/50 bg-indigo-500/15"
                  : "border-[var(--c-line)] bg-[var(--c-surface)] hover:border-indigo-400/40 hover:bg-indigo-500/10"
              }`}
            >
              <span className={`lfm-zzz text-xl text-indigo-600 dark:text-indigo-200 ${character.isSleeping ? "is-asleep" : ""}`}><i>z</i><i>z</i><i>Z</i></span>
              <span className="min-w-0 flex-1">
                <span className="block text-xs font-bold text-[var(--c-ink)]">{character.isSleeping ? "Asleep" : "Put to sleep"}</span>
                <span className="block text-[11px] text-[var(--c-ink-3)]">
                  {character.isSleeping
                    ? "Hidden from lockouts, raid rosters and Discord pings. Click to wake."
                    : "Hide from lockouts, raid rosters and Discord pings. History stays."}
                </span>
              </span>
              <span className={`relative h-5 w-9 shrink-0 rounded-full transition-colors ${character.isSleeping ? "bg-indigo-500" : "bg-[var(--c-sunken-2)]"}`}>
                <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all ${character.isSleeping ? "left-[18px]" : "left-0.5"}`} />
              </span>
            </button>
          )}
          {guildMatches.length > 0 && (
            <label className="mb-3 flex cursor-pointer items-center gap-3 rounded-xl border border-[var(--c-accent-line)] bg-[var(--c-accent-soft)] px-3 py-2.5">
              <input
                type="checkbox"
                checked={addToGuildOn}
                onChange={(e) => setAddToGuildOn(e.target.checked)}
                className="h-4 w-4 accent-[var(--c-accent)]"
              />
              <span className="text-xs text-[var(--c-ink-2)]">
                Also add to{" "}
                {guildMatches.length === 1 ? (
                  <span className="font-bold text-[var(--c-accent-ink)]">{guildMatches[0].guildName}</span>
                ) : (
                  <select
                    value={addToGuildId}
                    onChange={(e) => setAddToGuildId(e.target.value)}
                    className="rounded border border-[var(--c-line)] bg-[var(--c-surface)] px-1 py-0.5 text-xs font-bold text-[var(--c-accent-ink)]"
                  >
                    {guildMatches.map((m) => <option key={m.guildId} value={m.guildId}>{m.guildName}</option>)}
                  </select>
                )}
                <span className="text-[var(--c-ink-3)]"> · you're already a member, same realm and faction</span>
              </span>
            </label>
          )}
          <button
            type="submit"
            form="charForm"
            disabled={isSubmitting}
            className="char-modal-tone w-full py-3 font-bold text-xs uppercase tracking-widest rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ backgroundColor: accent, color: isLightHex(accent) ? "#111" : "#fff" }}
          >
            {ctaText}
          </button>
        </div>
      </div>
    </div>
  );
}

// --- MAIN COMPONENT ---

export default function RosterPage({
  characters,
  setCharacters,
  accountAvailability,
  setAccountAvailability,
  availabilityConfirmation,
  setAvailabilityConfirmation,
  weeklyAvailability,
  setWeeklyAvailability,
}: RosterPageProps) {
  legible = useTheme().onLight;
  const timezone = useTimezone();
  const { confirm } = useConfirm();
  const { showToast } = useToast();
  
  const [games, setGames] = useState<Game[]>([]);
  // Label each character's game only when they span more than one (e.g. Anniversary + Forever).
  const multiGame = new Set(characters.map((c: any) => c.gameId ?? c.game_id).filter(Boolean)).size > 1;
  const [fieldsByGame, setFieldsByGame] = useState<Record<string, Group[]>>({});

  // Which day this account's availability week opens on. Availability is stored
  // per user, so one grid covers every character; it opens on whichever region
  // resets earliest (Tuesday if any character is US), and the copy below the
  // grid names both days when they differ. See src/lib/regionReset.ts.
  const accountResetRule = useMemo(
    () => ruleForAccount(characters, Object.values(fieldsByGame).flat()),
    [characters, fieldsByGame]
  );
  const accountResetDays = useMemo(
    () => resetDayNamesForAccount(characters, Object.values(fieldsByGame).flat()),
    [characters, fieldsByGame]
  );

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"create" | "edit">("create");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [bisModalCharId, setBisModalCharId] = useState<string | null>(null);
  const [guildModalChar, setGuildModalChar] = useState<any>(null);
  const [crossGuildStatus, setCrossGuildStatus] = useState<boolean>(false);
  const [crossGuildLoading, setCrossGuildLoading] = useState(false);
  const [crossGuildMap, setCrossGuildMap] = useState<Record<string, boolean>>({});

  const [me, setMe] = useState<any | null>(null);
  const [meLoaded, setMeLoaded] = useState(false);

  // Loot heat per owned character (only chars the council awarded in the last
  // 30 days are returned; others get no badge). Keyed by character id.
  const [heatByCharId, setHeatByCharId] = useState<Record<string, { heat: number; parse_score: number }>>({});

  // Guild join applications (Discord "join through guild" flow), keyed by
  // character id. Pending shows on the tile's guild strip with a Withdraw
  // action; a recent rejection shows a dismissible notice.
  const [joinRequestsByCharId, setJoinRequestsByCharId] = useState<Record<string, {
    id: string; status: string; guildName: string; guildTag: string | null;
  }>>({});
  const [dismissedRejections, setDismissedRejections] = useState<Record<string, boolean>>(() => {
    try { return JSON.parse(localStorage.getItem("lfm_dismissed_join_rejections") || "{}"); } catch { return {}; }
  });
  const loadJoinRequests = useCallback(() => {
    fetch("/me/join-requests", { credentials: "include" })
      .then(r => r.ok ? r.json() : [])
      .then((rows: any[]) => {
        const map: Record<string, { id: string; status: string; guildName: string; guildTag: string | null }> = {};
        for (const row of Array.isArray(rows) ? rows : []) {
          if (!row.characterId) continue;
          // Pending wins over an older rejection on the same character
          if (map[row.characterId] && row.status !== "pending") continue;
          map[row.characterId] = { id: row.id, status: row.status, guildName: row.guildName, guildTag: row.guildTag };
        }
        setJoinRequestsByCharId(map);
      })
      .catch(() => {});
  }, []);
  useEffect(() => { loadJoinRequests(); }, [loadJoinRequests]);
  const dismissRejection = (requestId: string) => {
    setDismissedRejections(prev => {
      const next = { ...prev, [requestId]: true };
      try { localStorage.setItem("lfm_dismissed_join_rejections", JSON.stringify(next)); } catch {}
      return next;
    });
  };

  // New state to track which card is currently recalculating
  const [refreshingId, setRefreshingId] = useState<string | null>(null);
  
// Join guild state
const [joinCode, setJoinCode] = useState("");
// The player's guilds and their active games, for "also add to <guild>" at creation.
const [myGuildGames, setMyGuildGames] = useState<MyGuildGame[]>([]);
useEffect(() => {
  fetch("/me/guild-games", { credentials: "include" })
    .then((r) => (r.ok ? r.json() : []))
    .then(setMyGuildGames)
    .catch(() => setMyGuildGames([]));
}, [characters]);

// Journey page (opened from a character card) and the sleep toggle.
const [journeyCharId, setJourneyCharId] = useState<string | null>(null);
const toggleSleep = async (char: any) => {
  const sleeping = !char.isSleeping;
  // Optimistic: the card flips at once, reverted if the server says no.
  setCharacters((prev: any[]) => prev.map((c: any) => c.id === char.id ? { ...c, isSleeping: sleeping, sleptAt: sleeping ? new Date().toISOString() : null } : c));
  try {
    const res = await fetch(`/characters/${char.id}/sleep`, {
      method: "PUT", credentials: "include", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ sleeping }),
    });
    if (!res.ok) throw new Error();
    showToast(sleeping ? `${char.name} is asleep. zZz` : `${char.name} is awake again`, "success");
  } catch {
    setCharacters((prev: any[]) => prev.map((c: any) => c.id === char.id ? { ...c, isSleeping: !sleeping } : c));
    showToast("Could not change sleep", "error");
  }
};

// Guildless characters that match a guild the player is already in.
const [guildSuggestions, setGuildSuggestions] = useState<{ characterId: string; characterName: string; guildId: string; guildName: string }[]>([]);
const [addingSuggestion, setAddingSuggestion] = useState<string | null>(null);
const loadGuildSuggestions = useCallback(async () => {
  try {
    const r = await fetch("/me/guild-suggestions", { credentials: "include" });
    if (r.ok) setGuildSuggestions(await r.json());
  } catch {
    /* the strip just stays hidden */
  }
}, []);
useEffect(() => { loadGuildSuggestions(); }, [loadGuildSuggestions, characters]);
const addSuggestedCharacter = async (sg: { characterId: string; characterName: string; guildId: string; guildName: string }) => {
  setAddingSuggestion(sg.characterId);
  try {
    const res = await fetch(`/guilds/${sg.guildId}/add-character`, {
      method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ characterId: sg.characterId }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) { showToast(data.message || "Could not add the character", "error"); return; }
    showToast(`${sg.characterName} added to ${sg.guildName}`, "success");
    const charRes = await fetch("/characters", { credentials: "include" });
    if (charRes.ok) setCharacters(await charRes.json());
  } catch {
    showToast("Network error", "error");
  } finally {
    setAddingSuggestion(null);
  }
};
const [joinCharId, setJoinCharId] = useState("");

// Setup celebration state
const [showCelebration, setShowCelebration] = useState(false);
const [hasCheckedOnboarding, setHasCheckedOnboarding] = useState(false);
const availabilityDebounceRef = useRef<NodeJS.Timeout | null>(null);

// Weekly availability UI state
const [selectedWeek, setSelectedWeek] = useState<'current' | 'next'>('next');
const activeAvailabilityWeek = selectedWeek === 'current' ? weeklyAvailability.currentWeek : weeklyAvailability.nextWeek;
const activeWeekStart = activeAvailabilityWeek?.weekStart || (selectedWeek === 'current' ? getCurrentWednesday() : getNextWednesday());
const rosterTimezoneLabel = useMemo(() => {
  try {
    return new Intl.DateTimeFormat(undefined, {
      timeZone: timezone,
      timeZoneName: "short",
    })
      .formatToParts(new Date())
      .find((p) => p.type === "timeZoneName")?.value || timezone;
  } catch {
    return timezone;
  }
}, [timezone]);

// Squad state
const [squad, setSquad] = useState<Squad | null>(null);
const [squadLoading, setSquadLoading] = useState(true);
const [squadJoinCode, setSquadJoinCode] = useState('');
const [squadCreateName, setSquadCreateName] = useState('');
const [showSquadCreateModal, setShowSquadCreateModal] = useState(false);
const [squadActionLoading, setSquadActionLoading] = useState(false);
const [squadAvailability, setSquadAvailability] = useState<SquadAvailabilityData | null>(null);
const [squadAvailWeek, setSquadAvailWeek] = useState<'current' | 'next'>('next');
const [powerHourExpanded, setPowerHourExpanded] = useState(false);
const [selectedSquadMember, setSelectedSquadMember] = useState<SquadMember | null>(null);
const [memberProfileData, setMemberProfileData] = useState<any>(null);
const [memberProfileLoading, setMemberProfileLoading] = useState(false);

  // Fetch cross-guild status for all guilded characters
  useEffect(() => {
    const guildIds = Array.from(new Set(characters.filter((c: any) => c.guildId).map((c: any) => c.guildId)));
    guildIds.forEach(gId => {
      fetch(`/api/guilds/${gId}/members/cross-guild`, { credentials: 'include' })
        .then(r => r.ok ? r.json() : { allowCrossGuild: false })
        .then(d => setCrossGuildMap(prev => ({ ...prev, [gId]: d.allowCrossGuild || false })))
        .catch(() => {});
    });
  }, [characters]);

  // --- BOOTSTRAP ---
  useEffect(() => {
    fetch("/me", { credentials: "include" })
      .then(r => r.ok ? r.json() : null)
      .then(data => { setMe(data); setMeLoaded(true); })
      .catch(() => { setMe(null); setMeLoaded(true); });

    fetch("/me/availability", { credentials: "include" })
      .then(r => r.ok ? r.json() : {})
      .then((data: any) => {
        if (data && data.slots) setAccountAvailability(bitstringToBoolArray(data.slots));
      });

    fetch("/me/character-heat", { credentials: "include" })
      .then(r => r.ok ? r.json() : { heat: {} })
      .then((data: any) => { if (data && data.heat) setHeatByCharId(data.heat); })
      .catch(() => {});

    // Fetch squad info
    fetch("/api/squads/mine", { credentials: "include" })
      .then(r => r.ok ? r.json() : { squad: null })
      .then((data: any) => { 
        setSquad(data.squad);
        setSquadLoading(false);
        // Fetch availability if user is in a squad
        if (data.squad) {
          fetch(`/api/squads/${data.squad.id}/availability?week=next`, { credentials: 'include' })
            .then(r => r.ok ? r.json() : null)
            .then(avail => { if (avail) setSquadAvailability(avail); })
            .catch(() => {});
        }
      })
      .catch(() => setSquadLoading(false));
  }, [setAccountAvailability]);

  useEffect(() => {
    if (!meLoaded || !me) return;
    (async () => {
      try {
        const gRes = await fetch("/games", { credentials: "include" });
        const gData = await gRes.json();
        setGames(gData);

        const fieldsTemp: Record<string, Group[]> = {};
        for (const g of gData) {
          try {
            const fRes = await fetch(`/games/${g.id}/fields`, { credentials: "include" });
            fieldsTemp[g.id] = await fRes.json();
          } catch {}
        }
        setFieldsByGame(fieldsTemp);

        const cRes = await fetch("/characters", { credentials: "include" });
        if (cRes.ok) setCharacters(await cRes.json());
      } catch (e) { console.error(e); }
    })();
  }, [meLoaded, me, setCharacters]);

  // --- ACTIONS ---

  // Squad Actions
  const handleCreateSquad = async () => {
    if (!squadCreateName.trim()) return;
    setSquadActionLoading(true);
    try {
      const res = await fetch('/api/squads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name: squadCreateName.trim() })
      });
      const data = await res.json();
      if (!res.ok) {
        showToast(data.error === 'already_in_squad' ? 'You are already in a squad' : 'Failed to create squad', 'error');
        return;
      }
      // Refresh squad data
      const squadRes = await fetch('/api/squads/mine', { credentials: 'include' });
      const squadData = await squadRes.json();
      setSquad(squadData.squad);
      setShowSquadCreateModal(false);
      setSquadCreateName('');
      showToast('Squad created!', 'success');
    } catch (err) {
      showToast('Failed to create squad', 'error');
    } finally {
      setSquadActionLoading(false);
    }
  };

  const handleJoinSquad = async () => {
    if (!squadJoinCode.trim()) return;
    setSquadActionLoading(true);
    try {
      const res = await fetch('/api/squads/join', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ inviteCode: squadJoinCode.trim() })
      });
      const data = await res.json();
      if (!res.ok) {
        const msg = data.error === 'already_in_squad' ? 'You are already in a squad' :
                    data.error === 'invalid_invite_code' ? 'Invalid invite code' : 'Failed to join squad';
        showToast(msg, 'error');
        return;
      }
      // Refresh squad data
      const squadRes = await fetch('/api/squads/mine', { credentials: 'include' });
      const squadData = await squadRes.json();
      setSquad(squadData.squad);
      setSquadJoinCode('');
      showToast(`Joined ${data.squadName}!`, 'success');
    } catch (err) {
      showToast('Failed to join squad', 'error');
    } finally {
      setSquadActionLoading(false);
    }
  };

  const handleLeaveSquad = async () => {
    if (!squad) return;
    const confirmed = await confirm({
      title: 'Leave Squad?',
      description: `Are you sure you want to leave "${squad.name}"?${squad.isOwner && squad.members.length > 1 ? ' Leadership will transfer to another member.' : squad.isOwner ? ' The squad will be deleted.' : ''}`,
      confirmText: 'Leave',
      variant: 'danger'
    });
    if (!confirmed) return;
    
    setSquadActionLoading(true);
    try {
      const res = await fetch(`/api/squads/${squad.id}/leave`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (!res.ok) {
        showToast('Failed to leave squad', 'error');
        return;
      }
      setSquad(null);
      showToast('Left squad', 'success');
    } catch (err) {
      showToast('Failed to leave squad', 'error');
    } finally {
      setSquadActionLoading(false);
    }
  };

  const handleRegenerateCode = async () => {
    if (!squad) return;
    setSquadActionLoading(true);
    try {
      const res = await fetch(`/api/squads/${squad.id}/regenerate-code`, {
        method: 'POST',
        credentials: 'include'
      });
      const data = await res.json();
      if (!res.ok) {
        showToast('Failed to regenerate code', 'error');
        return;
      }
      setSquad(prev => prev ? { ...prev, inviteCode: data.inviteCode } : null);
      showToast('New invite code generated', 'success');
    } catch (err) {
      showToast('Failed to regenerate code', 'error');
    } finally {
      setSquadActionLoading(false);
    }
  };

  const fetchMemberProfile = useCallback(async (member: SquadMember) => {
    if (!squad) return;
    setSelectedSquadMember(member);
    setMemberProfileLoading(true);
    setMemberProfileData(null);
    try {
      const res = await fetch(`/api/squads/${squad.id}/members/${member.userId}/profile`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setMemberProfileData(data);
      }
    } catch {} finally {
      setMemberProfileLoading(false);
    }
  }, [squad]);

  const fetchSquadAvailability = useCallback(async (squadId: string, week: 'current' | 'next') => {
    try {
      const res = await fetch(`/api/squads/${squadId}/availability?week=${week}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setSquadAvailability(data);
      }
    } catch {}
  }, []);

  const handleKickMember = async (memberUserId: string, memberName: string) => {
    if (!squad) return;
    const confirmed = await confirm({
      title: 'Remove Member?',
      description: `Are you sure you want to remove ${memberName} from the squad?`,
      confirmText: 'Remove',
      variant: 'danger'
    });
    if (!confirmed) return;

    setSquadActionLoading(true);
    try {
      const res = await fetch(`/api/squads/${squad.id}/members/${memberUserId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (!res.ok) {
        showToast('Failed to remove member', 'error');
        return;
      }
      // Refresh squad data
      const squadRes = await fetch('/api/squads/mine', { credentials: 'include' });
      const squadData = await squadRes.json();
      setSquad(squadData.squad);
      showToast('Member removed', 'success');
    } catch (err) {
      showToast('Failed to remove member', 'error');
    } finally {
      setSquadActionLoading(false);
    }
  };

  const getSelectedOptionsForChar = (char: any, groupKey: string): Option[] => {
    const gid = char.gameId ?? char.game_id;
    const groups = fieldsByGame[gid] || [];
    const group = groups.find(g => g.key === groupKey);
    if (!group) return [];
    const selectedIds = (char.options || []).filter((o: any) => o.group_id === group.id).map((o: any) => o.option_id);
    return group.options.filter(opt => selectedIds.includes(opt.id));
  };

  function openCreateModal() {
    setEditingId(null);
    setModalMode("create");
    setModalOpen(true);
  }

  function openEditModal(charId: string) {
    const current = characters.find((c: any) => c.id === charId);
    if (!current) return;
    setEditingId(charId);
    setModalMode("edit");
    setModalOpen(true);
  }

  const handleCharacterSubmit = async (values: CharacterFormValues): Promise<string | null> => {
    const trimmedName = values.name.trim();
    if (!trimmedName) return "Character name is required";
    if (trimmedName.length < 2) return "Character name must be at least 2 characters";
    if (trimmedName.length > 24) return "Character name must be 24 characters or less";

    const groups = fieldsByGame[values.gameId] || [];
    const requiredKeys = ["class", "spec", "realm", "faction", "region"];
    const missingFields: string[] = [];
    for (const group of groups) {
      if (!requiredKeys.includes(group.key)) continue;
      const optionValue = values.options[group.id];
      const hasValue = Array.isArray(optionValue)
        ? optionValue.length > 0
        : optionValue !== undefined && optionValue !== null && Number(optionValue) > 0;
      if (!hasValue) missingFields.push(group.label || group.key);
    }
    if (missingFields.length > 0) return `Please select: ${missingFields.join(", ")}`;

    const payloadOptions: Record<number, number | number[]> = {};
    for (const [gidStr, val] of Object.entries(values.options)) {
      const gid = Number(gidStr);
      if (Array.isArray(val)) {
        if (val.length > 0) payloadOptions[gid] = val;
      } else if (val > 0) payloadOptions[gid] = val;
    }
    const existingChar = editingId ? characters.find((c) => c.id === editingId) : null;
    const existingMeta = existingChar?.metadata || {};
    const body = {
      gameId: values.gameId,
      name: trimmedName,
      options: payloadOptions,
      metadata: { ...existingMeta, wcl_url: values.wclUrl },
    };
    const url = modalMode === "create" ? "/characters" : `/characters/${editingId}`;
    const method = modalMode === "create" ? "POST" : "PUT";

    try {
      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: "unknown_error" }));
        if (errorData.message) return errorData.message;
        if (errorData.error === "missing_required_fields") {
          return `Missing required fields: ${errorData.fields?.join(", ") || "unknown"}`;
        }
        if (errorData.error === "invalid_role_for_class") {
          return "Selected role is not valid for this class";
        }
        return "Failed to save character. Please try again.";
      }

      // Created, and the player chose to put it straight into one of their guilds.
      let addedToGuild: string | null = null;
      if (modalMode === "create" && values.addToGuildId) {
        const created = await response.json().catch(() => null);
        if (created?.id) {
          const add = await fetch(`/guilds/${values.addToGuildId}/add-character`, {
            method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ characterId: created.id }),
          });
          const addData = await add.json().catch(() => ({}));
          if (add.ok) addedToGuild = myGuildGames.find((g) => g.guildId === values.addToGuildId)?.guildName || "your guild";
          else showToast(addData.message || "Character created, but it could not be added to the guild", "error");
        }
      }

      const res = await fetch("/characters", { credentials: "include" });
      if (res.ok) setCharacters(await res.json());
      setModalOpen(false);
      showToast(
        addedToGuild ? `Character created and added to ${addedToGuild}!`
          : modalMode === "create" ? "Character created!" : "Character updated!"
      );

      if (modalMode === "create") {
        setTimeout(() => checkOnboardingStatus(), 500);
        setTimeout(() => requestOnboardingTour(trimmedName), 700);
      }
      return null;
    } catch (e) {
      console.error("Submission failed:", e);
      return "Network error. Please try again.";
    }
  };

  // --- RECALCULATE LOGIC ---
  const handleRecalculate = async (char: any) => {
    setRefreshingId(char.id);
    
    try {
        // 1. Reconstruct Options Payload from existing character data
        const payloadOptions: Record<number, number | number[]> = {};
        if (Array.isArray(char.options)) {
            for (const entry of char.options) {
                const gid = entry.group_id;
                const optId = entry.option_id;
                // We need to know if this group is multi-select to form the payload correctly
                // We check fieldsByGame for the char's game ID
                const gidDef = (fieldsByGame[char.gameId ?? char.game_id] || []).find(g => g.id === gid);
                
                if (gidDef?.multiSelect) {
                    const existing = (payloadOptions[gid] as number[]) || [];
                    payloadOptions[gid] = [...existing, optId];
                } else {
                    payloadOptions[gid] = optId;
                }
            }
        }

        // 2. Prepare Metadata: REMOVE 'render_url' to force re-fetch
        const cleanMetadata = { ...char.metadata };
        delete cleanMetadata.render_url; // This triggers the backend BNET fetch
        delete cleanMetadata.equipment;
        delete cleanMetadata.equipment_last_updated;
        // Note: We keep wcl_url, which triggers the backend WCL fetch

        const body = {
            gameId: char.gameId ?? char.game_id,
            name: char.name,
            options: payloadOptions,
            metadata: cleanMetadata
        };

        // 3. Send Update
        await fetch(`/characters/${char.id}`, { 
            method: "PUT", 
            headers: { "Content-Type": "application/json" }, 
            credentials: "include", 
            body: JSON.stringify(body) 
        });

        // 4. Refresh List
        const res = await fetch("/characters", { credentials: "include" });
        if (res.ok) {
            setCharacters(await res.json());
            showToast(`${char.name} updated successfully!`);
        }

    } catch (e) {
        console.error("Recalculate failed", e);
        showToast("Update failed. Please try again.", "error");
    } finally {
        setRefreshingId(null);
    }
  };

  // Check onboarding status (debounced - called after painting stops)
  const checkOnboardingStatus = useCallback(async () => {
    if (hasCheckedOnboarding) return; // Only check once per session
    
    try {
      const res = await fetch("/me/onboarding-status", { credentials: "include" });
      if (!res.ok) return;
      
      const data = await res.json();
      
      // Show celebration if eligible (has char + availability, hasn't seen it)
      if (data.eligible && !data.completed) {
        setHasCheckedOnboarding(true);
        if ((window as any).__lfmTourActive) {
          // The guided tour paints availability as one of its steps and ends
          // with its own "Ready to Raid" finale, so don't stack a second
          // celebration on top of it. Record it as seen instead.
          fetch("/me/onboarding-complete", { method: "POST", credentials: "include" }).catch(() => {});
          return;
        }
        setShowCelebration(true);
      }
    } catch (err) {
      console.error("Failed to check onboarding status:", err);
    }
  }, [hasCheckedOnboarding]);

  const handleCelebrationDismiss = useCallback(async () => {
    setShowCelebration(false);
    // Mark onboarding as complete in database
    try {
      await fetch("/me/onboarding-complete", {
        method: "POST",
        credentials: "include",
      });
    } catch (err) {
      console.error("Failed to mark onboarding complete:", err);
    }
  }, []);

  const handleAvailabilityChange = (next: boolean[]) => {
    setAccountAvailability(next);
    
    // Determine which week we're editing
    const activeWeek = selectedWeek === 'current' 
      ? weeklyAvailability.currentWeek 
      : weeklyAvailability.nextWeek;
    const weekStart = activeWeek?.weekStart || (selectedWeek === 'current' ? getCurrentWednesday() : getNextWednesday());
    const newSlots = boolArrayToBitstring(next);

    // Optimistically update weeklyAvailability BEFORE the fetch so the grid
    // prop never reverts to stale data (which caused a visual flash)
    const optimisticWeek: WeekAvailability = {
      weekStart: weekStart,
      weekEnd: activeWeek?.weekEnd || formatWeekRange(weekStart).split(' - ')[1] || '',
      slots: newSlots,
      confirmedAt: activeWeek?.confirmedAt || null,
      confirmedVia: activeWeek?.confirmedVia || null,
    };
    if (selectedWeek === 'current') {
      setWeeklyAvailability({ ...weeklyAvailability, currentWeek: optimisticWeek });
    } else {
      setWeeklyAvailability({ ...weeklyAvailability, nextWeek: optimisticWeek });
    }
    
    fetch("/me/availability", {
      method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include",
      body: JSON.stringify({ 
        slots: newSlots,
        weekStart: weekStart,
        confirm: true
      }),
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.confirmedWeekStart || data?.weekStart) {
          const confirmedWeek = data.confirmedWeekStart || data.weekStart;
          setAvailabilityConfirmation({
            confirmedWeekStart: confirmedWeek,
            confirmedAt: new Date().toISOString(),
            confirmedVia: 'web'
          });
          
          // Reconcile with server response (adds confirmed timestamp)
          const confirmedWeekData: WeekAvailability = {
            weekStart: confirmedWeek,
            weekEnd: formatWeekRange(confirmedWeek).split(' - ')[1] || '',
            slots: newSlots,
            confirmedAt: new Date().toISOString(),
            confirmedVia: 'web'
          };
          
          if (selectedWeek === 'current') {
            setWeeklyAvailability(prev => ({ ...prev, currentWeek: confirmedWeekData }));
          } else {
            setWeeklyAvailability(prev => ({ ...prev, nextWeek: confirmedWeekData }));
          }
        }
      })
      .catch(console.error);
    
    // Debounced check for onboarding celebration
    if (availabilityDebounceRef.current) {
      clearTimeout(availabilityDebounceRef.current);
    }
    availabilityDebounceRef.current = setTimeout(() => {
      checkOnboardingStatus();
    }, 1500);
  };

  // The week the grid is showing. Shared by the grid and the tour hooks below.
  const gridWeek: boolean[] = selectedWeek === 'current'
    ? (weeklyAvailability.currentWeek?.slots ? bitstringToBoolArray(weeklyAvailability.currentWeek.slots) : accountAvailability)
    : (weeklyAvailability.nextWeek?.slots ? bitstringToBoolArray(weeklyAvailability.nextWeek.slots) : accountAvailability);
  const gridHasHours = gridWeek.some(Boolean);

  // --- Guided tour hooks ---------------------------------------------------
  // The tour (OnboardingTour) runs outside this page, so it talks to us through
  // window events: whether it is running, and quick-fill requests from its
  // availability step.
  const [tourActive, setTourActive] = useState<boolean>(() => !!(window as any).__lfmTourActive);
  useEffect(() => {
    const onActive = (e: Event) => setTourActive(!!(e as CustomEvent).detail?.active);
    window.addEventListener("lfm:tour-active", onActive);
    return () => window.removeEventListener("lfm:tour-active", onActive);
  }, []);

  // Latest values for the quick-fill listener, so it never paints a stale week.
  const quickFillRef = useRef({ gridWeek, activeWeekStart, timezone, apply: handleAvailabilityChange });
  quickFillRef.current = { gridWeek, activeWeekStart, timezone, apply: handleAvailabilityChange };
  useEffect(() => {
    const onFill = (e: Event) => {
      const preset = TOUR_QUICK_FILLS.find((p) => p.key === (e as CustomEvent).detail?.preset);
      if (!preset) return;
      const { gridWeek: base, activeWeekStart: weekStart, timezone: tz, apply } = quickFillRef.current;
      // Adds to what is already painted; never wipes hours the player set.
      const next = base.length === 336 ? [...base] : [...base, ...new Array(336 - base.length).fill(false)];
      for (const day of preset.daysMon0) {
        for (let slot = preset.fromSlot; slot < preset.toSlot; slot++) {
          next[localCellToBitIdx(weekStart, day, slot, tz)] = true;
        }
      }
      apply(next);
    };
    window.addEventListener("lfm:tour-quickfill", onFill);
    return () => window.removeEventListener("lfm:tour-quickfill", onFill);
  }, []);

  // `weekKey` lets the readiness card confirm a specific week without first
  // flipping the week tabs; the availability section still passes nothing.
  const handleConfirmAvailability = async (weekKey?: 'current' | 'next') => {
    const targetWeek = weekKey || selectedWeek;
    const activeWeek = targetWeek === 'current'
      ? weeklyAvailability.currentWeek
      : weeklyAvailability.nextWeek;
    const weekStart = activeWeek?.weekStart || (targetWeek === 'current' ? getCurrentWednesday() : getNextWednesday());

    try {
      const res = await fetch("/me/availability/confirm", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ weekStart })
      });
      if (res.ok) {
        const data = await res.json();
        const confirmedWeek = data.confirmedWeekStart || data.weekStart;
        setAvailabilityConfirmation({
          confirmedWeekStart: confirmedWeek,
          confirmedAt: new Date().toISOString(),
          confirmedVia: 'web'
        });
        
        // Update the weekly availability state
        const updatedWeek: WeekAvailability = {
          weekStart: confirmedWeek,
          weekEnd: activeWeek?.weekEnd || formatWeekRange(confirmedWeek).split(' - ')[1] || '',
          slots: activeWeek?.slots || '',
          ...activeWeek,
          confirmedAt: new Date().toISOString(),
          confirmedVia: 'web',
        };
        if (targetWeek === 'current') {
          setWeeklyAvailability(prev => ({ ...prev, currentWeek: updatedWeek }));
        } else {
          setWeeklyAvailability(prev => ({ ...prev, nextWeek: updatedWeek }));
        }
        
        showToast(`Availability confirmed for ${formatWeekRange(confirmedWeek)}!`, 'success');
      }
    } catch (err) {
      console.error('Failed to confirm availability:', err);
      showToast('Failed to confirm availability', 'error');
    }
  };

  const handleDeclineAvailability = async () => {
    const activeWeek = selectedWeek === 'current'
      ? weeklyAvailability.currentWeek
      : weeklyAvailability.nextWeek;
    const weekStart = activeWeek?.weekStart || (selectedWeek === 'current' ? getCurrentWednesday() : getNextWednesday());

    try {
      const res = await fetch("/me/availability/decline", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ weekStart })
      });
      if (res.ok) {
        const data = await res.json();
        const declinedWeek = data.declinedWeekStart || data.weekStart || weekStart;
        const nowIso = new Date().toISOString();

        setAvailabilityConfirmation({
          confirmedWeekStart: declinedWeek,
          confirmedAt: nowIso,
          confirmedVia: 'declined'
        });

        // Reconcile the weekly grid state so the toggle flips immediately.
        const updatedWeek: WeekAvailability = {
          weekStart: declinedWeek,
          weekEnd: activeWeek?.weekEnd || formatWeekRange(declinedWeek).split(' - ')[1] || '',
          slots: activeWeek?.slots || '',
          confirmedAt: nowIso,
          confirmedVia: 'declined'
        };
        if (selectedWeek === 'current') {
          setWeeklyAvailability(prev => ({ ...prev, currentWeek: updatedWeek }));
        } else {
          setWeeklyAvailability(prev => ({ ...prev, nextWeek: updatedWeek }));
        }

        showToast(`Marked unavailable for ${formatWeekRange(declinedWeek)}`, 'success');
      } else {
        showToast('Failed to mark unavailable', 'error');
      }
    } catch (err) {
      console.error('Failed to decline availability:', err);
      showToast('Failed to mark unavailable', 'error');
    }
  };

  const handleCopyToNextWeek = async () => {
    try {
      const res = await fetch("/me/availability/copy-to-next", {
        method: "POST",
        credentials: "include"
      });
      if (res.ok) {
        const data = await res.json();
        // Refresh availability data
        const availRes = await fetch("/me/availability", { credentials: "include" });
        if (availRes.ok) {
          const availData = await availRes.json();
          if (availData.currentWeek || availData.nextWeek) {
            setWeeklyAvailability({
              currentWeek: availData.currentWeek,
              nextWeek: availData.nextWeek
            });
          }
          if (availData.slots) {
            setAccountAvailability(bitstringToBoolArray(availData.slots));
          }
        }
        showToast('Availability copied and confirmed for next week!', 'success');
      }
    } catch (err) {
      console.error('Failed to copy availability:', err);
      showToast('Failed to copy availability', 'error');
    }
  };

  // --- READINESS CHECKLIST (first tile of the character grid) ---

  // Ring the destination in the onboarding tour's amber on the way. The
  // checklist sends you a long way down a long page, and without the highlight
  // you arrive with no idea which section answered the row you clicked.
  const scrollToSection = useCallback((id: string) => {
    const el = document.getElementById(id);
    if (el) flashSpotlight(el, { radius: SPOTLIGHT_RADII[id] ?? 16 });
  }, []);

  // Same handoff the character tile's guild modal uses: stash the id, switch tab.
  const openGuildPage = useCallback((guildId: string) => {
    sessionStorage.setItem("openGuildId", guildId);
    window.location.hash = "Guilds";
  }, []);

  const readiness = useMemo(() => {
    // "Ready to Raid" isn't a stored flag — a character is raid-ready once it
    // has at least one raid interest picked, which is what the edit modal's
    // checkbox actually gates. Same source, so the two can't disagree.
    const countRaidInterests = (c: any) =>
      getSelectedOptionsForChar(c, "raid_interest").length +
      getSelectedOptionsForChar(c, "raid_preferences").length;

    const base = computeReadiness({
      accountAvailability,
      weeklyAvailability,
      // Sleeping characters are left out of the checklist.
      characters: characters.filter((c: any) => !c.isSleeping),
      squad,
      countRaidInterests,
      // So "This week (Aug 11)" names the day the week really opened for this
      // account, not the Wednesday storage label.
      resetRule: accountResetRule,
    });

    // The shared checklist says what each row is about; this page decides what
    // clicking it does. PlayerReadyBar maps the same targets to navigation.
    const handlerFor = (target?: ReadyTarget): (() => void) | undefined => {
      if (!target) return undefined;
      switch (target.kind) {
        case "availability":
          return () => scrollToSection("availability");
        case "confirm-week":
          return () => handleConfirmAvailability(target.week);
        case "raid-interests":
          return target.charId ? () => openEditModal(target.charId!) : undefined;
        case "guild":
          return target.openGuildId
            ? () => openGuildPage(target.openGuildId!)
            : () => scrollToSection("join-guild");
        case "join-guild":
          return () => scrollToSection("join-guild");
        case "squad":
          return () => scrollToSection("squad");
        case "bis":
          return target.charId ? () => setBisModalCharId(target.charId!) : undefined;
        default:
          return undefined;
      }
    };

    const rows: ReadyRow[] = base.rows.map((r) => ({
      key: r.key,
      label: r.label,
      value: r.value,
      state: r.state,
      actionLabel: r.actionLabel,
      ctaLabel: r.ctaLabel,
      optional: r.optional,
      onClick: handlerFor(r.target),
      ctaOnClick: handlerFor(r.ctaTarget),
    }));

    const ctaAction = base.cta ? handlerFor(base.cta.target) : undefined;
    const cta = base.cta && ctaAction ? { label: base.cta.label, onClick: ctaAction } : null;

    return { rows, syncLabel: base.syncLabel, cta };
    // fieldsByGame matters: raid interests can't be resolved until the game
    // option definitions have loaded, which happens after characters do.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountAvailability, weeklyAvailability, characters, squad, fieldsByGame, scrollToSection, openGuildPage]);

  // Handoff from the home page's readiness bar. It can't open a modal that
  // lives on this page, so it parks the character id and we pick it up once
  // the roster and the game option definitions have actually loaded.
  useEffect(() => {
    if (!characters.length || Object.keys(fieldsByGame).length === 0) return;
    const editId = sessionStorage.getItem("openCharEditId");
    if (editId) {
      sessionStorage.removeItem("openCharEditId");
      if (characters.some((c: any) => c.id === editId)) {
        openEditModal(editId);
        return;
      }
    }
    const bisId = sessionStorage.getItem("openCharBisId");
    if (bisId) {
      sessionStorage.removeItem("openCharBisId");
      if (characters.some((c: any) => c.id === bisId)) setBisModalCharId(bisId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [characters, fieldsByGame]);

    if (!meLoaded) return <div className="p-10 text-center text-[var(--c-ink-3)] animate-pulse">Initializing...</div>;

    if (!me) return (
    <div className="flex flex-col items-center justify-center min-h-[400px] text-center space-y-6">
        <div className="max-w-md space-y-2">
        <h2 className="text-3xl text-[var(--c-ink)]" style={DISPLAY}>Sign in to see your profile</h2>
        <p className="text-[var(--c-ink-2)]">
            Connect your Discord to manage your personal roster, track character performance, and set your weekly availability.
        </p>
        </div>
        <a 
        href={`${API_URL}/auth/discord`}
        className="rounded-xl bg-[var(--c-accent)] px-6 py-3 text-[15px] font-bold text-[var(--c-ink-on-fill)] shadow-[var(--c-shadow)] hover:bg-[var(--c-accent-hover)] transition"
        >
        Sign in with Discord
        </a>
    </div>
    );

    return (
      <div className="max-w-7xl mx-auto space-y-8 pb-24 animate-in fade-in slide-in-from-bottom-4 duration-500">
      
      {/* HEADER */}
      <header className="flex items-end justify-between gap-6 pt-2 sm:pt-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.3em] text-[var(--c-accent-ink)]">Profile</div>
          <h1 className="mt-2 text-4xl leading-tight text-[var(--c-ink)] sm:text-5xl" style={DISPLAY}>Your characters</h1>
          <div className="mt-4 h-px w-28 bg-gradient-to-r from-[var(--c-accent)] to-transparent" />
          <p className="mt-4 text-[15px] text-[var(--c-ink-2)]">Manage your characters and when you can raid.</p>
        </div>
      </header>

      {/* CHARACTER CARDS */}
      <section>
        {/* Join Guild Bar & New Recruit */}
        <div id="join-guild" data-tour="guild" className="p-4 bg-emerald-500/10 dark:bg-emerald-950/40 border border-emerald-500/20 rounded-xl flex items-center gap-4 mb-6">
            <div className="flex items-center gap-2 ml-1">
                <svg className="w-5 h-5 text-emerald-700 dark:text-emerald-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z" /></svg>
                <span className="text-sm font-bold text-emerald-700 dark:text-emerald-300">Join Guild</span>
                <div className="relative group">
                    <span className="w-4 h-4 rounded-full bg-emerald-500/20 text-emerald-700/70 dark:text-emerald-400/70 text-[10px] font-bold flex items-center justify-center cursor-help hover:bg-emerald-500/30 hover:text-emerald-700 dark:hover:text-emerald-300 transition-colors">?</span>
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-[var(--c-surface)] shadow-[var(--c-shadow-lg)] border border-[var(--c-line-strong)] rounded-lg text-xs text-[var(--c-ink)] whitespace-nowrap opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-opacity z-50 shadow-xl">
                        Guild leaders can generate invite codes from their guild settings
                        <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-black/95"></div>
                    </div>
                </div>
            </div>
            <input 
                value={joinCode} onChange={e => setJoinCode(e.target.value)} placeholder="Enter invite code..."
                className="bg-[var(--c-surface)] border border-emerald-500/20 rounded-lg px-3 py-2 text-sm text-[var(--c-ink)] placeholder-[var(--c-ink-4)] outline-none focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/20 w-40 transition-colors"
            />
            <div className="flex items-center gap-1">
                <svg className="w-4 h-4 text-[var(--c-ink-4)] shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0" /></svg>
                <select 
                    value={joinCharId} onChange={e => setJoinCharId(e.target.value)}
                    className="bg-[var(--c-surface)] border border-emerald-500/20 rounded-lg px-3 py-2 text-sm text-[var(--c-ink)] outline-none focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/20 w-52 transition-colors"
                >
                    <option value="">Select Character...</option>
                    {characters.filter(c => !c.guildId).map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                </select>
            </div>
            <button 
                onClick={async () => {
                    if (!joinCode || !joinCharId) return;
                    try {
                        const res = await fetch(`${API_URL}/guilds/join`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            credentials: 'include',
                            body: JSON.stringify({ inviteCode: joinCode, characterId: joinCharId }),
                        });
                        const data = await res.json();
                        if (!res.ok) {
                          const msg = data.message || (
                              data.error === 'invalid_invite' ? 'Invalid invite code' : 
                              data.error === 'already_member' ? 'Already a member' : 
                              data.error === 'realm_mismatch' ? 'Character realm doesn\'t match guild server' :
                              'Join failed'
                          );
                          showToast(msg, 'error');
                          return;
                      }
                        showToast('Successfully joined guild!', 'success');
                        setJoinCode('');
                        setJoinCharId('');
                        // Refresh characters to show new guild membership
                        const charRes = await fetch("/characters", { credentials: "include" });
                        if (charRes.ok) setCharacters(await charRes.json());
                        // A code join supersedes any pending application on this character
                        loadJoinRequests();
                    } catch { showToast('Network error', 'error'); }
                }}
                disabled={!joinCode || !joinCharId} 
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-bold rounded-lg transition-colors flex items-center gap-1.5 shadow-lg shadow-emerald-900/30"
            >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9" /></svg>
                Join
            </button>

            {/* Spacer & New Recruit */}
            <div className="flex-1" />
            <div className="w-px h-6 bg-[var(--c-sunken-2)] mx-1" />
            <button 
                onClick={openCreateModal} 
                className="px-4 py-1.5 bg-cyan-600 hover:bg-cyan-500 text-white text-xs font-bold uppercase tracking-wide rounded shadow-lg shadow-cyan-900/20 transition-all"
            >
                + New Character
            </button>
        </div>
        {/* Characters that already match a guild you're in (same game, region,
            realm and faction): add them with one click, no invite code needed. */}
        {guildSuggestions.length > 0 && (
          <div className="-mt-3 mb-6 flex flex-wrap items-center gap-2 rounded-xl border border-[var(--c-accent-line)] bg-[var(--c-accent-soft)] px-4 py-3">
            <span className="text-xs font-bold text-[var(--c-accent-ink)]">Ready to add</span>
            {guildSuggestions.map((sg) => (
              <button
                key={`${sg.characterId}:${sg.guildId}`}
                onClick={() => addSuggestedCharacter(sg)}
                disabled={addingSuggestion !== null}
                className="rounded-full border border-[var(--c-line)] bg-[var(--c-surface)] px-3 py-1 text-xs font-semibold text-[var(--c-ink)] shadow-[var(--c-shadow-sm)] transition hover:border-[var(--c-accent)] disabled:opacity-50"
              >
                + Add <span className="font-bold">{sg.characterName}</span> to {sg.guildName}
              </button>
            ))}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            <RosterReadinessTile rows={readiness.rows} syncLabel={readiness.syncLabel} cta={readiness.cta} />

            {/* Awake characters first; sleeping ones settle at the end. */}
            {[...characters].sort((a: any, b: any) => Number(!!a.isSleeping) - Number(!!b.isSleeping)).map((char: any) => {
                const gameId = char.gameId ?? char.game_id;
                const groups = fieldsByGame[gameId] || [];
                const classOpts = getSelectedOptionsForChar(char, "class");
                const roleOpts = getSelectedOptionsForChar(char, "role");
                const genericGroups = groups.filter(g => !["class","role","name","raid_preferences","raid_interest"].includes(g.key));
                const className = classOpts[0]?.label || "Unknown";
                const classColor = classOpts[0]?.metadata?.color || stringToColor(className);
                const wclPerf = char.metadata?.wcl_perf;
                const perfScore = wclPerf?.score || null;
                const perfColor = perfScore ? getParseColor(perfScore) : null;
                
                const avatarImage = char.metadata?.render_url as string | undefined;
                const isRefreshing = refreshingId === char.id;

                const realmLabel =
                  genericGroups
                    .find((g) => g.key === "realm")
                    ?.options.find((o) => getSelectedOptionsForChar(char, "realm").includes(o))
                    ?.label || "Unknown Realm";

                return (
                    <RosterCharacterTile
                        key={char.id}
                        char={char}
                        gameLabel={multiGame ? shortGameName(games.find((g) => g.id === (char.gameId ?? char.game_id))?.name) : null}
                        onJourney={() => setJourneyCharId(char.id)}
                        classLabel={className}
                        classColor={classColor}
                        roleOpts={roleOpts}
                        realmLabel={realmLabel}
                        wclPerf={wclPerf}
                        perfScore={perfScore}
                        perfColor={perfColor}
                        heat={heatByCharId[char.id]}
                        avatarImage={avatarImage}
                        isRefreshing={isRefreshing}
                        onEdit={() => openEditModal(char.id)}
                        onRefresh={() => handleRecalculate(char)}
                        onBis={() => setBisModalCharId(char.id)}
                        joinRequest={(() => {
                          const jr = joinRequestsByCharId[char.id];
                          if (!jr) return null;
                          if (jr.status === "rejected" && dismissedRejections[jr.id]) return null;
                          return jr;
                        })()}
                        onWithdrawRequest={async () => {
                          const jr = joinRequestsByCharId[char.id];
                          if (!jr) return;
                          const ok = await confirm({
                            title: "Withdraw application?",
                            description: `${char.name} will no longer be waiting to join ${jr.guildName}. The character stays on your roster.`,
                            confirmText: "Withdraw",
                            variant: "danger",
                          });
                          if (!ok) return;
                          await fetch(`/guilds/join-requests/${jr.id}/cancel`, { method: "POST", credentials: "include" });
                          loadJoinRequests();
                        }}
                        onDismissRejection={() => {
                          const jr = joinRequestsByCharId[char.id];
                          if (jr) dismissRejection(jr.id);
                        }}
                        onGuildOpen={() => setGuildModalChar(char)}
                        onDelete={async () => {
                            const isConfirmed = await confirm({
                                title: "Delete character?",
                                description: `Permanently delete ${char.name}? This cannot be undone.`,
                                confirmText: "Delete",
                                variant: "danger",
                            });
                            if (!isConfirmed) return;
                            await fetch(`/characters/${char.id}`, { method: "DELETE", credentials: "include" });
                            const res = await fetch("/characters", { credentials: "include" });
                            if (res.ok) setCharacters(await res.json());
                        }}
                    />
                );
            })}

            {/* Example character, shown only while the guided tour runs for a
                player with no characters, so the Sync and BiS steps have a
                card to point at. Nothing here is saved and every action is a
                no-op that explains itself. */}
            {tourActive && characters.length === 0 && (() => {
              const explain = () => showToast("This is an example. Use + New Character to add your own.", "info");
              return (
                <div className="relative">
                  <span className="pointer-events-none absolute -top-2.5 left-1/2 z-40 -translate-x-1/2 rounded-full border border-amber-400/60 bg-amber-500 px-3 py-0.5 text-[10px] font-black uppercase tracking-[0.16em] text-black shadow">
                    Example
                  </span>
                  <RosterCharacterTile
                    char={TOUR_EXAMPLE_CHARACTER}
                    classLabel="Paladin"
                    classColor={getWowClassColor("Paladin")}
                    roleOpts={[{ id: -1, key: "healer", label: "Healer", metadata: {} }]}
                    realmLabel="Your realm"
                    wclPerf={null}
                    perfScore={null}
                    perfColor={null}
                    heat={null}
                    avatarImage={undefined}
                    isRefreshing={false}
                    onEdit={explain}
                    onRefresh={explain}
                    onBis={explain}
                    onGuildOpen={explain}
                    onDelete={explain}
                  />
                </div>
              );
            })()}
        </div>
      </section>

      {/* WEEKLY AVAILABILITY */}
      <section id="availability" data-tour="availability" className="mt-12 pt-8 border-t border-[var(--c-line)]">
        <div className="flex items-start justify-between mb-4">
          <div>
          <h2 className="text-2xl text-[var(--c-ink)]" style={DISPLAY}>Weekly availability</h2>
          <p className="text-sm text-[var(--c-ink-2)] mt-1">
              Set when you can raid. Applies to <strong>all characters</strong>.
            </p>
          </div>
        </div>

        {/* Week Tabs */}
        <div data-tour="avail-weeks" className="flex gap-2 mb-4">
          <button 
            onClick={() => setSelectedWeek('current')}
            className={`px-4 py-2 rounded-lg font-medium transition-all border ${
              selectedWeek === 'current' 
              ? 'bg-[var(--c-sunken-2)] border-[var(--c-line-strong)] text-[var(--c-ink)]' 
                : 'bg-[var(--c-sunken)] border-[var(--c-line)] text-[var(--c-ink-2)] hover:bg-[var(--c-sunken-2)] hover:text-[var(--c-ink)]'
            }`}
          >
            <span className="text-base font-bold">This Week</span>
            <span className="text-sm opacity-70 ml-2">
              {weeklyAvailability.currentWeek?.weekStart 
                ? formatWeekRange(weeklyAvailability.currentWeek.weekStart)
                : ''}
            </span>
          </button>
          
          <button 
            onClick={() => setSelectedWeek('next')}
            className={`px-4 py-2 rounded-lg font-medium transition-all border ${
              selectedWeek === 'next' 
                ? 'bg-cyan-600 border-cyan-500 text-white' 
                : 'bg-[var(--c-sunken)] border-[var(--c-line)] text-[var(--c-ink-2)] hover:bg-[var(--c-sunken-2)] hover:text-[var(--c-ink)]'
            }`}
          >
            <span className="text-base font-bold">Next Week</span>
            <span className="text-sm opacity-70 ml-2">
              {weeklyAvailability.nextWeek?.weekStart 
                ? formatWeekRange(weeklyAvailability.nextWeek.weekStart)
                : ''}
            </span>
          </button>

          {/* Use Current Week — only on Next Week tab */}
          {selectedWeek === 'next' && weeklyAvailability.currentWeek?.slots && (
            <button 
              onClick={handleCopyToNextWeek}
              className="px-4 py-2.5 bg-cyan-600/30 hover:bg-cyan-600/50 text-cyan-800 dark:text-cyan-200 text-sm font-bold rounded-lg transition-colors border border-cyan-500/40 ml-auto"
            >
              ⧉ Copy current week
            </button>
          )}
        </div>

        {/* Toggles Row — two separate cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          {/* CARD 1: Availability Status — 3 states: pending, confirmed, declined */}
          <div data-tour="avail-confirm" className="rounded-xl border border-[var(--c-line)] bg-[var(--c-bg-veil)] p-4">
          {(() => {
            const activeWeek = selectedWeek === 'current' ? weeklyAvailability.currentWeek : weeklyAvailability.nextWeek;
            const isDeclined = activeWeek?.confirmedVia === 'declined';
            const isConfirmed = !!activeWeek?.confirmedAt && !isDeclined;
            // Toggle semantics: ON = confirmed available, OFF = unavailable (declined or pending).
            // Clicking flips the state directly — no disabled state.
            const handleToggle = () => {
              if (isConfirmed) {
                handleDeclineAvailability();
              } else {
                handleConfirmAvailability();
              }
            };

            // Visual state
            const trackClass = isConfirmed
              ? 'bg-emerald-500'
              : isDeclined
              ? 'bg-rose-500/70 hover:bg-rose-500'
              : 'bg-[var(--c-sunken-2)] hover:bg-[var(--c-line-strong)]';
            const labelClass = isConfirmed
              ? 'text-emerald-700 dark:text-emerald-400'
              : isDeclined
              ? 'text-rose-700 dark:text-rose-300'
              : 'text-[var(--c-ink)]';
            const label = isConfirmed
              ? 'Available This Reset'
              : isDeclined
              ? 'Marked Unavailable'
              : 'Availability Unset';
            const sublabel = isConfirmed
              ? `Confirmed ${new Date(activeWeek!.confirmedAt!).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}. Tap to mark unavailable`
              : isDeclined
              ? `You won't be rostered this reset. Tap to confirm instead`
              : 'Tap ON to confirm you\'re available, or leave OFF to sit this reset out';

            return (
              <div className="flex items-center gap-3">
                <button
                  onClick={handleToggle}
                  className={`relative w-12 h-6 rounded-full transition-colors duration-200 shrink-0 cursor-pointer ${trackClass}`}
                  title={isConfirmed ? 'Click to mark unavailable' : 'Click to confirm available'}
                >
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform duration-200 ${isConfirmed ? 'translate-x-6' : ''}`} />
                </button>
                <div className="flex flex-col">
                  <span className={`text-sm font-bold ${labelClass}`}>
                    {label}
                  </span>
                  <span className="text-xs text-[var(--c-ink-3)]">
                    {sublabel}
                  </span>
                </div>
              </div>
            );
          })()}
          </div>

          {/* CARD 2: Cross-Guild Visibility — one toggle per guild, plus one per
              guildless character. A guilded character's visibility is owned by its
              guild (opt in here, guild releases the week); a guildless one is a free
              agent and only needs its own opt-in plus availability for the week. */}
          {characters.length > 0 && (
          <div className="rounded-xl border border-[var(--c-line)] bg-[var(--c-bg-veil)] p-4 flex flex-col gap-3">
          {(() => {
            const guildIds = Array.from(new Set(characters.filter((c: any) => c.guildId).map((c: any) => c.guildId)));
            return guildIds.map(gId => {
              const guildChar = characters.find((c: any) => c.guildId === gId);
              const guildName = guildChar?.guildName || 'Guild';
              const guildTag = guildChar?.guildTag;
              const isEnabled = crossGuildMap[gId] || false;
              return (
                <div key={gId} className="flex items-center gap-3">
                  <button
                    onClick={async () => {
                      const newVal = !isEnabled;
                      try {
                        const res = await fetch(`/api/guilds/${gId}/members/cross-guild`, {
                          method: 'PUT',
                          headers: { 'Content-Type': 'application/json' },
                          credentials: 'include',
                          body: JSON.stringify({ allowCrossGuild: newVal }),
                        });
                        if (res.ok) setCrossGuildMap(prev => ({ ...prev, [gId]: newVal }));
                      } catch (err) { console.error(err); }
                    }}
                    className={`relative w-12 h-6 rounded-full transition-colors duration-200 shrink-0 ${isEnabled ? 'bg-cyan-500' : 'bg-[var(--c-sunken-2)] hover:bg-[var(--c-line-strong)]'}`}
                  >
                    <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform duration-200 ${isEnabled ? 'translate-x-6' : ''}`} />
                  </button>
                  <div className="flex flex-col">
                    <span className={`text-sm font-bold ${isEnabled ? 'text-cyan-700 dark:text-cyan-300' : 'text-[var(--c-ink)]'}`}>
                      Open to Other Raid Teams
                    </span>
                    <span className="text-xs text-[var(--c-ink-3)]">
                      {guildTag ? `[${guildTag}] ` : ''}{guildName}: {isEnabled ? 'visible when released' : 'not visible to other guilds'}
                    </span>
                  </div>
                </div>
              );
            });
          })()}
          {characters.filter((c: any) => !c.guildId).map((c: any) => {
            const isEnabled = c.freeAgent !== false;
            return (
              <div key={`fa-${c.id}`} className="flex items-center gap-3">
                <button
                  onClick={async () => {
                    const newVal = !isEnabled;
                    try {
                      const res = await fetch(`/characters/${c.id}/free-agent`, {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        credentials: 'include',
                        body: JSON.stringify({ freeAgent: newVal }),
                      });
                      if (res.ok) {
                        setCharacters((prev: any[]) =>
                          prev.map((p: any) => (p.id === c.id ? { ...p, freeAgent: newVal } : p))
                        );
                      }
                    } catch (err) { console.error(err); }
                  }}
                  className={`relative w-12 h-6 rounded-full transition-colors duration-200 shrink-0 ${isEnabled ? 'bg-teal-500' : 'bg-[var(--c-sunken-2)] hover:bg-[var(--c-line-strong)]'}`}
                >
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform duration-200 ${isEnabled ? 'translate-x-6' : ''}`} />
                </button>
                <div className="flex flex-col">
                  <span className={`text-sm font-bold ${isEnabled ? 'text-teal-700 dark:text-teal-300' : 'text-[var(--c-ink)]'}`}>
                    Open to Other Raid Teams
                  </span>
                  <span className="text-xs text-[var(--c-ink-3)]">
                    {c.name} (no guild): {isEnabled
                      ? 'listed as a free agent in any week you confirm availability'
                      : 'not visible to any guild'}
                  </span>
                </div>
              </div>
            );
          })}
          </div>
          )}
        </div>

        {/* Availability Grid. The data-tour-* attributes let the guided tour
            see whether hours are painted and name the player's timezone. */}
        <div
          data-tour="avail-grid"
          data-tour-painted={gridHasHours ? "true" : "false"}
          data-tour-tz={rosterTimezoneLabel}
          className="bg-[var(--c-surface)] p-5 rounded-xl border border-[var(--c-line-strong)] shadow-inner"
        >
          <div className="mb-3 flex justify-end">
            <span
              className="inline-flex items-center gap-2 rounded-md border border-cyan-400/30 bg-cyan-400/10 px-2.5 py-1 text-xs font-semibold text-cyan-800 dark:text-cyan-200"
              title={timezone}
            >
              <span>Timezone:</span>
              <span>{rosterTimezoneLabel}</span>
            </span>
          </div>
          <AvailabilityGrid
            week={gridWeek}
            weekStartIso={activeWeekStart}
            userTimezone={timezone}
            resetWeekdayIso={accountResetRule.weekday}
            onChange={handleAvailabilityChange}
          />
          <p className="mt-3 text-xs text-[var(--c-ink-3)]">
            {accountResetDays.length > 1
              ? `Week starts ${resetDayName(accountResetRule.weekday)}. Your ${accountResetDays.join(" and ")} characters reset on different days, so this grid opens on the earliest. Drag across the grid to add a session.`
              : `Week starts ${resetDayName(accountResetRule.weekday)} to match raid reset. Drag across the grid to add a session.`}
          </p>
        </div>
      </section>

      {/* SQUAD SECTION */}
      <section id="squad" data-tour="squad" className="mt-12 pt-8 border-t border-[var(--c-line)]">
        <div className="flex items-start justify-between mb-4">
          <div>
          <h2 className="text-2xl text-[var(--c-ink)] flex items-center gap-2" style={DISPLAY}>
              <svg className="w-6 h-6 text-indigo-600 dark:text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
              Your Squad
              <div className="relative group">
                <span className="w-5 h-5 rounded-full bg-indigo-500/20 text-indigo-600/70 dark:text-indigo-400/70 text-[10px] font-bold flex items-center justify-center cursor-help hover:bg-indigo-500/30 hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors">?</span>
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-4 py-3 bg-[var(--c-surface)] shadow-[var(--c-shadow-lg)] border border-[var(--c-line-strong)] rounded-lg text-xs text-[var(--c-ink)] w-72 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-opacity z-50 shadow-xl normal-case tracking-normal font-normal leading-relaxed">
                  <span className="font-bold text-indigo-700 dark:text-indigo-300">Squads</span> help you play with your friends. Create a squad and share the code, or join one with a code from a friend. Allowing automatic features and Raid Leaders to identify your connections.
                  <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-black/95"></div>
                </div>
              </div>
            </h2>
            <p className="text-sm text-[var(--c-ink-2)] mt-1">
              Squad up with friends to get seated together in raids.
            </p>
          </div>
        </div>

        {squadLoading ? (
          <div className="bg-[var(--c-surface)] p-6 rounded-xl border border-[var(--c-line)] text-center">
            <div className="animate-pulse text-[var(--c-ink-3)]">Loading squad info...</div>
          </div>
        ) : squad ? (
          /* STATE B: IN A SQUAD */
          <div className="bg-[var(--c-surface)] rounded-xl border border-[var(--c-line)]">

            {/* ═══ ZONE 1: HEADER ═══ */}
            <div className="px-6 py-4 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 min-w-0">
                <h3 className="text-lg font-bold text-[var(--c-ink)] truncate">{squad.name}</h3>
                <span className="text-xs text-[var(--c-ink-4)] shrink-0">{squad.members.length} member{squad.members.length !== 1 ? 's' : ''}</span>
                {squad.isOwner && <span className="px-1.5 py-0.5 rounded bg-indigo-500/15 text-indigo-700/70 dark:text-indigo-300/70 text-[9px] font-bold uppercase shrink-0">Owner</span>}
              </div>
              <div className="flex items-center gap-2 shrink-0">
              <button
                  onClick={() => { navigator.clipboard.writeText(squad.inviteCode); showToast('Code copied!', 'success'); }}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-[var(--c-sunken)] hover:bg-[var(--c-sunken)] border border-[var(--c-line-soft)] transition-all group/code relative"
                  title=""
                >
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-[var(--c-surface)] shadow-[var(--c-shadow-lg)] border border-[var(--c-line-strong)] rounded-lg text-[10px] text-[var(--c-ink)] whitespace-nowrap opacity-0 pointer-events-none group-hover/code:opacity-100 transition-opacity z-50 shadow-xl">
                    Click to copy invite code
                    <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-black/95"></div>
                  </div>
                  <span className="text-xs font-mono font-bold text-[var(--c-ink-3)] group-hover/code:text-[var(--c-ink)] tracking-wider transition-colors">{squad.inviteCode}</span>
                  <svg className="w-3.5 h-3.5 text-[var(--c-ink-4)] group-hover/code:text-cyan-700 dark:group-hover/code:text-cyan-400 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </button>
                {squad.isOwner && (
                  <button onClick={handleRegenerateCode} disabled={squadActionLoading} className="p-1.5 rounded-md text-[var(--c-ink-4)] hover:text-amber-700 dark:hover:text-amber-300 hover:bg-[var(--c-sunken)] transition-all disabled:opacity-50 relative group/regen" title="">
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-[var(--c-surface)] shadow-[var(--c-shadow-lg)] border border-[var(--c-line-strong)] rounded-lg text-[10px] text-[var(--c-ink)] whitespace-nowrap opacity-0 pointer-events-none group-hover/regen:opacity-100 transition-opacity z-50 shadow-xl">
                    Generate new code
                    <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-black/95"></div>
                  </div>
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                </button>
                )}
              </div>
            </div>

            {/* ═══ ZONE 2: SUMMARY STRIP ═══ */}
            <div className="px-6 pb-4">
              <div
                className="flex items-center gap-4 cursor-pointer"
                onClick={() => { if (squadAvailability && squadAvailability.membersWithAvailability > 0) setPowerHourExpanded(prev => !prev); }}
              >
                {/* Member avatars */}
                <div className="flex items-center -space-x-2">
                  {squad.members.slice(0, 6).map(member => {
                    const mc = member.mainCharacter;
                    const color = mc?.classColor || '#6366f1';
                    return (
                      <button
                        key={member.userId}
                        onClick={(e) => { e.stopPropagation(); fetchMemberProfile(member); }}
                        className="relative w-9 h-9 rounded-full border-2 border-[var(--c-surface)] shrink-0 hover:scale-110 hover:z-10 transition-transform bg-[var(--c-surface)] group/member"
                        style={{ boxShadow: `0 0 0 1px ${color}30` }}
                        title=""
                      >
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2.5 py-1 bg-[var(--c-surface)] shadow-[var(--c-shadow-lg)] border border-[var(--c-line-strong)] rounded-md text-[10px] text-[var(--c-ink)] whitespace-nowrap opacity-0 pointer-events-none group-hover/member:opacity-100 transition-opacity z-50 shadow-xl">
                          {member.displayName}
                          <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-black/95"></div>
                        </div>
                        <div className="w-full h-full rounded-full overflow-hidden">
                          {mc?.renderUrl ? (
                            <img src={mc.renderUrl} alt="" className="w-full h-full object-cover scale-150 translate-y-0.5" onError={(e) => { e.currentTarget.style.display='none'; (e.currentTarget.parentElement!.parentElement as HTMLElement).style.backgroundColor = color + '20'; }} />
                          ) : (
                            <span className="w-full h-full flex items-center justify-center text-xs font-bold" style={{ color: color + '80', backgroundColor: color + '15' }}>
                              {mc?.name?.charAt(0) || member.displayName.charAt(0)}
                            </span>
                          )}
                        </div>
                        {member.userId === squad.createdByUserId && (
                          <div className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full bg-amber-500/60 border border-[var(--c-surface)] flex items-center justify-center">
                            <svg className="w-1.5 h-1.5 text-[var(--c-ink)]" fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" /></svg>
                          </div>
                        )}
                      </button>
                    );
                  })}
                  {squad.members.length > 6 && (
                    <div className="w-9 h-9 rounded-full border-2 border-[var(--c-surface)] bg-[var(--c-sunken)] flex items-center justify-center text-[10px] text-[var(--c-ink-3)] font-bold shrink-0">+{squad.members.length - 6}</div>
                  )}
                </div>

                {/* Divider */}
                <div className="w-px h-8 bg-[var(--c-sunken)]" />

                {/* Best time highlight — or fallback message */}
                {squadAvailability && squadAvailability.membersWithAvailability > 0 ? (
                  squadAvailability.powerBlocks.length > 0 ? (
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className="flex-1 min-w-0">
                      <div className="text-[10px] text-[var(--c-ink-4)] uppercase tracking-wider font-bold mb-1.5">Best times</div>
                        <div className="flex items-center gap-2 flex-wrap">
                          {squadAvailability.powerBlocks.slice(0, 3).map((block, idx) => {
                            const durationSlots = block.end - block.start + 1;
                            const isPerfect = block.avgOverlap >= squadAvailability.totalMembers;
                            const timeLabel = formatBitRangeLocalLabel(
                              squadAvailability.targetWeek,
                              block.start,
                              block.end,
                              timezone
                            );
                            return (
                              <div key={idx} className={`flex items-center gap-2.5 px-3 py-1.5 rounded-lg border transition-all ${idx === 0 ? 'bg-[var(--c-sunken)] border-[var(--c-line)]' : 'bg-[var(--c-bg-veil)] border-[var(--c-line-soft)]'}`}>
                                <div className="flex flex-col">
                                  <span className={`text-sm font-bold tabular-nums ${isPerfect ? 'text-amber-700 dark:text-amber-300' : idx === 0 ? 'text-[var(--c-ink)]' : 'text-[var(--c-ink-3)]'}`}>
                                    {timeLabel}
                                  </span>
                                  <span className="text-[10px] text-[var(--c-ink-4)]">{Math.round(durationSlots/2)}h · {block.avgOverlap}/{squadAvailability.totalMembers}{isPerfect ? ' — everyone' : ''}</span>
                                </div>
                                {/* Duration bar */}
                                <div className="w-12 h-1.5 rounded-full bg-[var(--c-sunken)] overflow-hidden shrink-0">
                                  <div className={`h-full rounded-full ${isPerfect ? 'bg-amber-400/60' : 'bg-cyan-400/50'}`} style={{ width: `${Math.min(100, (durationSlots / 16) * 100)}%` }} />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      <svg className={`w-4 h-4 text-[var(--c-ink-4)] transition-transform duration-200 shrink-0 ${powerHourExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                      </svg>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3 flex-1">
                      <div>
                        <div className="text-[10px] text-[var(--c-ink-4)] uppercase tracking-wider font-bold mb-0.5">Availability</div>
                        <div className="text-sm text-[var(--c-ink-3)]">{squadAvailability.membersWithAvailability}/{squadAvailability.totalMembers} set — no shared window found yet</div>
                      </div>
                      <svg className={`w-4 h-4 text-[var(--c-ink-4)] transition-transform duration-200 shrink-0 ${powerHourExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                      </svg>
                    </div>
                  )
                ) : (
                  <div className="text-sm text-[var(--c-ink-4)]">No availability set yet</div>
                )}
              </div>
            </div>

            {/* ═══ ZONE 3: EXPANDABLE DETAIL ═══ */}
            {powerHourExpanded && squadAvailability && squadAvailability.membersWithAvailability > 0 && (
              <div className="px-6 pb-5 border-t border-[var(--c-line-soft)] pt-4 animate-in slide-in-from-top-2 duration-200">
                {/* Week toggle */}
                <div className="flex justify-end mb-3">
                  <div className="flex items-center gap-1 bg-[var(--c-surface)] rounded-lg p-0.5 border border-[var(--c-line-soft)]">
                    <button
                      onClick={() => { setSquadAvailWeek('current'); if (squad) fetchSquadAvailability(squad.id, 'current'); }}
                      className={`px-3 py-1 rounded-md text-xs font-bold transition-all ${squadAvailWeek === 'current' ? 'bg-[var(--c-sunken)] text-[var(--c-ink)] border border-[var(--c-line)]' : 'text-[var(--c-ink-4)] hover:text-[var(--c-ink-3)] border border-transparent'}`}
                    >This Week</button>
                    <button
                      onClick={() => { setSquadAvailWeek('next'); if (squad) fetchSquadAvailability(squad.id, 'next'); }}
                      className={`px-3 py-1 rounded-md text-xs font-bold transition-all ${squadAvailWeek === 'next' ? 'bg-[var(--c-sunken)] text-[var(--c-ink)] border border-[var(--c-line)]' : 'text-[var(--c-ink-4)] hover:text-[var(--c-ink-3)] border border-transparent'}`}
                    >Next Week</button>
                  </div>
                </div>

                {/* Heatmap */}
                <SquadOverlapHeatmap
                  overlap={squadAvailability.overlap}
                  maxOverlap={squadAvailability.maxOverlap}
                  totalMembers={squadAvailability.totalMembers}
                  members={squadAvailability.members}
                  weekStartIso={squadAvailability.targetWeek}
                  userTimezone={timezone}
                />
              </div>
            )}

            {/* Footer */}
            <div className="px-6 pb-3 pt-1 flex justify-end">
              <button
                onClick={handleLeaveSquad}
                disabled={squadActionLoading}
                className="text-[10px] text-[var(--c-ink-4)] hover:text-red-600 dark:hover:text-red-400 transition-colors disabled:opacity-50 flex items-center gap-1 py-1 px-2 rounded hover:bg-red-500/10"
              >
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75" /></svg>
                {squad.isOwner && squad.members.length === 1 ? 'Delete Squad' : 'Leave Squad'}
              </button>
            </div>
          </div>
        ) : (
          /* STATE A: NO SQUAD */
          <div className="bg-[var(--c-surface)] p-6 rounded-xl border border-[var(--c-line)]">
            <div className="flex flex-col sm:flex-row gap-5">
              {/* Create Squad Option */}
              <div className="flex-1 p-5 rounded-xl bg-indigo-500/10 dark:bg-indigo-950/30 border border-indigo-500/15 hover:border-indigo-500/30 transition-colors group">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-11 h-11 rounded-xl bg-indigo-500/15 border border-indigo-500/25 flex items-center justify-center group-hover:bg-indigo-500/25 transition-colors">
                    <svg className="w-5 h-5 text-indigo-600 dark:text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-[var(--c-ink)]">Create Squad</h4>
                    <p className="text-xs text-[var(--c-ink-3)]">Start a new squad and share the code</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowSquadCreateModal(true)}
                  className="w-full py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-bold transition-colors flex items-center justify-center gap-2 shadow-lg shadow-indigo-900/30"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" /></svg>
                  Create Squad
                </button>
              </div>

              {/* Join Squad Option */}
              <div className="flex-1 p-5 rounded-xl bg-emerald-500/10 dark:bg-emerald-950/30 border border-emerald-500/15 hover:border-emerald-500/30 transition-colors group">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-11 h-11 rounded-xl bg-emerald-500/15 border border-emerald-500/25 flex items-center justify-center group-hover:bg-emerald-500/25 transition-colors">
                    <svg className="w-5 h-5 text-emerald-700 dark:text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-[var(--c-ink)]">Join Squad</h4>
                    <p className="text-xs text-[var(--c-ink-3)]">Enter an invite code from a friend</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <input
                    value={squadJoinCode}
                    onChange={e => setSquadJoinCode(e.target.value.toUpperCase())}
                    placeholder="INVITE CODE"
                    maxLength={8}
                    className="flex-1 px-3 py-2.5 rounded-lg bg-[var(--c-surface)] border border-emerald-500/20 text-sm text-[var(--c-ink)] placeholder:text-[var(--c-ink-4)] focus:outline-none focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/20 font-mono uppercase tracking-wider transition-colors"
                  />
                  <button
                    onClick={handleJoinSquad}
                    disabled={!squadJoinCode.trim() || squadActionLoading}
                    className="px-5 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-bold transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5 shadow-lg shadow-emerald-900/30"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9" /></svg>
                    Join
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </section>

      {/* SQUAD CREATE MODAL */}
      {showSquadCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm animate-in fade-in" onClick={() => setShowSquadCreateModal(false)} />
          <div className="relative w-full max-w-md bg-[var(--c-surface)] border border-[var(--c-line)] rounded-xl shadow-2xl animate-in zoom-in-95 duration-200">
            <div className="p-5 border-b border-[var(--c-line)] bg-[var(--c-bg-veil)] flex justify-between items-center">
              <h3 className="text-sm font-bold uppercase tracking-widest text-[var(--c-ink)]">Create Squad</h3>
              <button onClick={() => setShowSquadCreateModal(false)} className="text-[var(--c-ink-3)] hover:text-[var(--c-ink)]">✕</button>
            </div>
            <div className="p-6 space-y-4">
              <div className="space-y-2">
                <label className="text-xs font-bold text-[var(--c-ink-3)] uppercase tracking-wider">Squad Name</label>
                <input
                  value={squadCreateName}
                  onChange={e => setSquadCreateName(e.target.value)}
                  placeholder="e.g. The A-Team"
                  maxLength={50}
                  className="w-full px-4 py-3 rounded-lg bg-[var(--c-surface)] border border-[var(--c-line)] text-[var(--c-ink)] placeholder:text-[var(--c-ink-4)] focus:outline-none focus:border-indigo-500/50"
                  autoFocus
                />
                <p className="text-xs text-[var(--c-ink-4)]">Choose a name for your squad (max 50 characters)</p>
              </div>
            </div>
            <div className="p-5 border-t border-[var(--c-line)] flex justify-end gap-3">
              <button
                onClick={() => setShowSquadCreateModal(false)}
                className="px-4 py-2 rounded-lg text-sm font-medium text-[var(--c-ink-2)] hover:text-[var(--c-ink)] hover:bg-[var(--c-sunken-2)] transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateSquad}
                disabled={!squadCreateName.trim() || squadActionLoading}
                className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {squadActionLoading ? 'Creating...' : 'Create Squad'}
              </button>
            </div>
          </div>
        </div>
      )}

      {modalOpen && (
        <CharacterFormModal
          key={modalMode === "edit" ? editingId || "edit" : "create"}
          mode={modalMode}
          games={games}
          fieldsByGame={fieldsByGame}
          character={modalMode === "edit" ? characters.find((c: any) => c.id === editingId) || null : null}
          onClose={() => setModalOpen(false)}
          onSubmit={handleCharacterSubmit}
          myGuildGames={myGuildGames}
          onToggleSleep={modalMode === "edit" && editingId ? () => {
            const ch = characters.find((c: any) => c.id === editingId);
            if (ch) toggleSleep(ch);
          } : undefined}
        />
      )}

      {journeyCharId && <CharacterJourney characterId={journeyCharId} onClose={() => setJourneyCharId(null)} />}

      {/* GUILD MANAGEMENT MODAL */}
      {guildModalChar && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setGuildModalChar(null)} />
          <div className="relative w-full max-w-md bg-[var(--c-surface)] border border-[var(--c-line)] rounded-xl shadow-2xl overflow-hidden">
            <div className="p-5 border-b border-[var(--c-line)] bg-[var(--c-bg-veil)]">
              <div className="flex items-center gap-4">
                {guildModalChar.guildLogoUrl ? (
                  <img src={guildModalChar.guildLogoUrl} alt="" className="w-12 h-12 rounded-lg object-cover border border-[var(--c-line)]" />
                ) : (
                  <div className="w-12 h-12 rounded-lg bg-amber-500/20 flex items-center justify-center text-lg font-bold text-amber-700 dark:text-amber-300 border border-amber-500/30">
                    {guildModalChar.guildTag || guildModalChar.guildName?.charAt(0) || 'G'}
                  </div>
                )}
                <div>
                  <h3 className="text-lg font-bold text-[var(--c-ink)]">{guildModalChar.guildName}</h3>
                  {guildModalChar.guildTag && <div className="text-xs text-[var(--c-ink-3)]">[{guildModalChar.guildTag}]</div>}
                </div>
              </div>
            </div>
            <div className="p-5 space-y-4">
              <div className="text-sm text-[var(--c-ink-2)]">
                <span className="font-bold text-[var(--c-ink)]">{guildModalChar.name}</span> is a member of this guild.
              </div>

              {/* Cross-Guild Opt-In Toggle */}
              <div className="flex items-center justify-between px-3 py-3 rounded-lg border border-[var(--c-line)] bg-[var(--c-bg-veil)]">
                <div className="flex flex-col gap-0.5 pr-3">
                  <span className="text-xs font-bold text-[var(--c-ink)]">Open to Other Raid Teams</span>
                  <span className="text-[10px] text-[var(--c-ink-3)]">If your guild releases unassigned players, other teams can see you</span>
                </div>
                <button
                  disabled={crossGuildLoading}
                  onClick={async () => {
                    const newVal = !crossGuildStatus;
                    setCrossGuildLoading(true);
                    try {
                      const res = await fetch(`/api/guilds/${guildModalChar.guildId}/members/cross-guild`, {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        credentials: 'include',
                        body: JSON.stringify({ allowCrossGuild: newVal }),
                      });
                      if (res.ok) { setCrossGuildStatus(newVal); setCrossGuildMap(prev => ({ ...prev, [guildModalChar.guildId]: newVal })); }
                    } catch (err) { console.error(err); }
                    finally { setCrossGuildLoading(false); }
                  }}
                  className={`relative w-10 h-5 rounded-full transition-colors duration-200 shrink-0 ${crossGuildLoading ? 'opacity-50' : ''} ${crossGuildStatus ? 'bg-cyan-500' : 'bg-[var(--c-sunken-2)]'}`}
                >
                  <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform duration-200 ${crossGuildStatus ? 'translate-x-5' : ''}`} />
                </button>
              </div>

              <div className="flex flex-col gap-2">
              <button
                  onClick={() => { 
                    sessionStorage.setItem('openGuildId', guildModalChar.guildId);
                    window.location.hash = 'Guilds'; 
                    setGuildModalChar(null); 
                  }}
                  className="w-full px-4 py-3 rounded-lg bg-cyan-500/10 border border-cyan-500/30 text-cyan-700 dark:text-cyan-300 font-semibold hover:bg-cyan-500/20 transition-all flex items-center justify-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                  View Guild Page
                </button>
                <button
                  onClick={async () => {
                    const isConfirmed = await confirm({
                      title: "Leave Guild?",
                      description: `Are you sure you want to remove ${guildModalChar.name} from ${guildModalChar.guildName}?`,
                      confirmText: "Leave Guild",
                      variant: "danger"
                    });
                    if (!isConfirmed) return;
                    try {
                      const res = await fetch('/guilds/leave', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        credentials: 'include',
                        body: JSON.stringify({ characterId: guildModalChar.id })
                      });
                      const data = await res.json();
                      if (res.ok) {
                        setGuildModalChar(null);
                        const refreshRes = await fetch("/characters", { credentials: "include" });
                        if (refreshRes.ok) setCharacters(await refreshRes.json());
                        showToast(`${guildModalChar.name} left the guild`);
                      } else {
                        showToast(data.error === 'owner_cannot_leave' ? 'Guild owners cannot leave. Transfer ownership first.' : 'Failed to leave guild', 'error');
                      }
                    } catch (err) {
                      showToast('Network error. Please try again.', 'error');
                    }
                  }}
                  className="w-full px-4 py-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-600 dark:text-red-400 font-semibold hover:bg-red-500/20 transition-all flex items-center justify-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  Leave Guild
                </button>
              </div>
            </div>
            <div className="p-4 border-t border-[var(--c-line)] bg-[var(--c-bg-veil)]">
              <button
                onClick={() => setGuildModalChar(null)}
                className="w-full px-4 py-2 rounded-lg bg-[var(--c-sunken)] text-[var(--c-ink-2)] font-medium hover:bg-[var(--c-sunken-2)] transition-all"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SQUAD MEMBER PROFILE MODAL */}
      {selectedSquadMember && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/85 backdrop-blur-md" onClick={() => { setSelectedSquadMember(null); setMemberProfileData(null); }} />
          <div className="relative w-full max-w-lg bg-[var(--c-surface)] border border-[var(--c-line)] rounded-2xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
            {/* Header with class art */}
            <div className="relative px-6 py-5 border-b border-[var(--c-line)] overflow-hidden">
              {(() => {
                const squadClassArt = getClassArtUrl(selectedSquadMember.mainCharacter?.className);
                return squadClassArt ? (
                <div className="absolute inset-0">
                  <img src={squadClassArt} alt="" className="w-full h-full object-cover opacity-[0.12]" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                  <div className="absolute inset-0 bg-gradient-to-r from-[var(--c-surface)] via-[var(--c-surface)] to-[var(--c-surface)]" />
                </div>
                ) : null;
              })()}
              <div className="relative flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div
                    className="w-14 h-14 rounded-full flex items-center justify-center text-xl font-black border-2"
                    style={{
                      backgroundColor: (selectedSquadMember.mainCharacter?.classColor || '#ffffff') + '15',
                      borderColor: (selectedSquadMember.mainCharacter?.classColor || '#ffffff') + '40',
                      color: selectedSquadMember.mainCharacter?.classColor || '#ffffff60'
                    }}
                  >
                    {selectedSquadMember.mainCharacter?.name?.charAt(0) || selectedSquadMember.displayName.charAt(0)}
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-[var(--c-ink)]">{selectedSquadMember.displayName}</h3>
                    <div className="text-xs text-[var(--c-ink-3)] mt-0.5">
                      {selectedSquadMember.characterCount} character{selectedSquadMember.characterCount !== 1 ? 's' : ''}
                      {selectedSquadMember.userId === squad?.createdByUserId && <span className="ml-2 px-1.5 py-0.5 rounded bg-indigo-500/20 border border-indigo-500/30 text-indigo-700 dark:text-indigo-300 text-[9px] font-bold uppercase">Owner</span>}
                    </div>
                  </div>
                </div>
                <button onClick={() => { setSelectedSquadMember(null); setMemberProfileData(null); }} className="text-[var(--c-ink-4)] hover:text-[var(--c-ink-2)] transition-colors p-1">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="p-6 max-h-[60vh] overflow-y-auto scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent">
              {memberProfileLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin w-6 h-6 border-2 border-[var(--c-line-strong)] border-t-cyan-400 rounded-full mx-auto" />
                  <p className="text-[var(--c-ink-4)] text-xs mt-3">Loading profile...</p>
                </div>
              ) : memberProfileData ? (
                <div className="space-y-5">
                  {/* Characters list */}
                  <div>
                    <div className="text-[10px] font-bold text-[var(--c-ink-4)] uppercase tracking-widest mb-3">Characters</div>
                    <div className="space-y-2">
                      {memberProfileData.characters.map((char: any) => (
                        <div key={char.id} className="flex items-center gap-3 p-3 rounded-xl bg-[var(--c-bg-veil)] border border-[var(--c-line-soft)] hover:border-[var(--c-line)] transition-colors">
                          {/* Avatar */}
                          <div
                            className="w-10 h-10 rounded-lg flex items-center justify-center text-sm font-black border shrink-0 overflow-hidden"
                            style={{
                              backgroundColor: (char.classColor || '#ffffff') + '15',
                              borderColor: (char.classColor || '#ffffff') + '30',
                              color: char.classColor || '#ffffff60'
                            }}
                          >
                            {char.renderUrl ? (
                              <img src={char.renderUrl} alt="" className="w-full h-full object-cover scale-150 translate-y-0.5" onError={(e) => { e.currentTarget.style.display='none'; (e.currentTarget.parentElement as HTMLElement).textContent = char.name.charAt(0); }} />
                            ) : char.name.charAt(0)}
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-bold text-[var(--c-ink)] truncate">{char.name}</span>
                              {char.className && (
                                <span className="text-[9px] px-1.5 py-0.5 rounded border font-bold uppercase tracking-wider shrink-0" style={{ borderColor: (char.classColor || '#ffffff') + '30', color: char.classColor || '#ffffff60', opacity: 0.7 }}>
                                  {char.className}
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Parse + Rating badges */}
                          <div className="flex items-center gap-2 shrink-0">
                            {char.wclScore != null && char.wclScore > 0 ? (
                              <a href={char.wclUrl || '#'} target="_blank" rel="noreferrer" className="flex items-center justify-center w-9 h-9 rounded-lg border text-xs font-black transition-transform hover:scale-110" style={{ backgroundColor: getParseColor(char.wclScore) + '15', borderColor: getParseColor(char.wclScore) + '40', color: getParseColor(char.wclScore) }}>
                                {char.wclScore}
                              </a>
                            ) : (
                              <div className="w-9 h-9 rounded-lg bg-[var(--c-bg-veil)] border border-[var(--c-line-soft)] flex items-center justify-center text-[9px] text-[var(--c-ink-4)]">—</div>
                            )}
                            {char.rating && char.rating.overallRating != null ? (
                              <div className="flex flex-col items-center">
                                <span className={`text-xs font-bold tabular-nums ${char.rating.overallRating >= 4 ? 'text-emerald-700 dark:text-emerald-400' : char.rating.overallRating >= 3 ? 'text-[var(--c-ink-2)]' : 'text-amber-700 dark:text-amber-400'}`}>{Number(char.rating.overallRating).toFixed(1)}</span>
                                <span className="text-[8px] text-[var(--c-ink-4)]">{char.rating.totalRatings} votes</span>
                              </div>
                            ) : null}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Availability mini-grid */}
                  {(() => {
                    const activeWeek = memberProfileData.availability?.nextWeek || memberProfileData.availability?.currentWeek || null;
                    const slots = activeWeek?.slots || null;
                    const weekStartIso = activeWeek?.weekStart || getCurrentWednesday();
                    if (!slots) return <div className="text-xs text-[var(--c-ink-4)] text-center py-3">No availability set</div>;
                    const DISPLAY_TO_DATA_MAP = [2,3,4,5,6,0,1];
                    const DAY_LABELS = ['W','T','F','S','S','M','T'];
                    const getActiveCountForDisplayDay = (displayDayIdx: number) => {
                      const dayLocalMon0 = DISPLAY_TO_DATA_MAP[displayDayIdx];
                      let active = 0;
                      for (let slot = 0; slot < 48; slot++) {
                        const idx = localCellToBitIdx(weekStartIso, dayLocalMon0, slot, timezone);
                        if (slots[idx] === '1') active++;
                      }
                      return active;
                    };
                    return (
                      <div>
                        <div className="text-[10px] font-bold text-[var(--c-ink-4)] uppercase tracking-widest mb-2">Availability</div>
                        <div className="grid grid-cols-7 gap-1">
                          {DAY_LABELS.map((d, di) => (
                            <div key={di} className="text-center">
                              <div className="text-[8px] text-[var(--c-ink-4)] font-bold mb-1">{d}</div>
                              <div className="h-8 rounded bg-[var(--c-surface)] overflow-hidden flex flex-col">
                                {(() => {
                                  const activeCount = getActiveCountForDisplayDay(di);
                                  const pct = activeCount / 48;
                                  return (
                                    <div className="flex-1 flex flex-col justify-end">
                                      <div className="rounded-t transition-all" style={{ height: `${pct * 100}%`, backgroundColor: `rgba(6, 182, 212, ${0.3 + pct * 0.6})` }} />
                                    </div>
                                  );
                                })()}
                              </div>
                              <div className="text-[8px] text-[var(--c-ink-4)] mt-0.5">{Math.round(getActiveCountForDisplayDay(di) / 2)}h</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              ) : (
                <div className="text-center py-8 text-[var(--c-ink-4)] text-sm">Failed to load profile</div>
              )}
            </div>

            {/* Footer with kick option for owner */}
            {squad?.isOwner && selectedSquadMember.userId !== me?.id && selectedSquadMember.userId !== squad.createdByUserId && (
              <div className="px-6 py-3 border-t border-[var(--c-line-soft)] flex justify-end">
                <button
                  onClick={() => {
                    handleKickMember(selectedSquadMember.userId, selectedSquadMember.displayName);
                    setSelectedSquadMember(null);
                    setMemberProfileData(null);
                  }}
                  className="text-[10px] text-[var(--c-ink-4)] hover:text-red-600 dark:hover:text-red-400 transition-colors flex items-center gap-1 px-2 py-1 rounded hover:bg-red-500/10"
                >
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                  Remove from squad
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      <SetupCelebration 
        show={showCelebration} 
        onDismiss={handleCelebrationDismiss}
      />
      {bisModalCharId && (() => {
        const c = characters.find((x: any) => x.id === bisModalCharId);
        if (!c) return null;
        const classOpts = getSelectedOptionsForChar(c, "class");
        return (
          <BisListModal
            open={true}
            onClose={() => setBisModalCharId(null)}
            character={{
              id: c.id,
              name: c.name,
              game_id: c.gameId ?? c.game_id,
              game_code: c.gameCode ?? c.game_code ?? null,
              class_name: classOpts[0]?.label ?? c.metadata?.class_name ?? null,
              equipment: c.metadata?.equipment ?? undefined,
              equipment_last_updated: c.metadata?.equipment_last_updated ?? null,
              bnet_equipment_profile_ns: c.metadata?.bnet_equipment_profile_ns ?? null,
              render_url: c.metadata?.render_url ?? null,
              spec_label: c.metadata?.primary_spec_label ?? null,
            }}
            canEdit={true}
            guildId={c.guildId ?? null}
            showToast={showToast}
          />
        );
      })()}
    </div>
  );
}

// --- SUB-COMPONENTS ---

function StatBadge({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div className={`px-3 py-1 rounded border text-xs font-mono flex gap-2 items-center ${color}`}>
        <span className="opacity-60 uppercase">{label}</span>
        <span className="font-bold">{value}</span>
    </div>
  );
}

function SquadOverlapHeatmap({
  overlap,
  maxOverlap,
  totalMembers,
  members,
  weekStartIso,
  userTimezone,
}: {
  overlap: number[];
  maxOverlap: number;
  totalMembers: number;
  members?: { userId: string; displayName: string; slots: string | null }[];
  weekStartIso: string;
  userTimezone: string;
}) {
  const DAYS_DISPLAY_LABELS = ['Wed', 'Thu', 'Fri', 'Sat', 'Sun', 'Mon', 'Tue'] as const;
  const DISPLAY_TO_DATA = [2, 3, 4, 5, 6, 0, 1];
  const [hoveredSlot, setHoveredSlot] = useState<{ dataIdx: number; day: string; hour: number; min: number } | null>(null);
  const [chainHighlight, setChainHighlight] = useState<Set<number>>(new Set());
  const heatmapRef = useRef<HTMLDivElement>(null);

  // When hovering, compute the contiguous chain of availability from this slot
  const handleSlotEnter = (dataIdx: number, day: string, hour: number, min: number) => {
    setHoveredSlot({ dataIdx, day, hour, min });
    
    if (overlap[dataIdx] === 0) {
      setChainHighlight(new Set());
      return;
    }
    
    // Walk left until chain breaks (same day only)
    const dayStart = Math.floor(dataIdx / 48) * 48;
    const dayEnd = dayStart + 47;
    const chain = new Set<number>();
    chain.add(dataIdx);
    
    for (let i = dataIdx - 1; i >= dayStart; i--) {
      if (overlap[i] > 0) chain.add(i); else break;
    }
    for (let i = dataIdx + 1; i <= dayEnd; i++) {
      if (overlap[i] > 0) chain.add(i); else break;
    }
    setChainHighlight(chain);
  };

  // Get names of members online at hovered slot
  const onlineNames = hoveredSlot && members
    ? members.filter(m => m.slots && m.slots[hoveredSlot.dataIdx] === '1').map(m => m.displayName)
    : [];

  return (
    <div className="overflow-x-auto pb-1 select-none relative" ref={heatmapRef}>
      <div className="min-w-[600px]">
      <div className="h-5 mb-1 flex items-center justify-between">
          {hoveredSlot ? (
            <div className="flex items-center gap-2 animate-in fade-in duration-100">
              <span className="text-xs font-mono text-cyan-700 dark:text-cyan-400 font-bold">{hoveredSlot.day} {hoveredSlot.hour.toString().padStart(2,'0')}:{hoveredSlot.min.toString().padStart(2,'0')}</span>
              <span className="text-[10px] text-[var(--c-ink-3)]">{overlap[hoveredSlot.dataIdx]}/{totalMembers} online</span>
              {onlineNames.length > 0 && onlineNames.length < totalMembers && members && (
                <span className="text-[10px] text-[var(--c-ink-4)]">· missing {members.filter(m => m.slots && m.slots[hoveredSlot.dataIdx] !== '1').map(m => m.displayName).join(', ')}</span>
              )}
            </div>
          ) : (
            <div className="text-[10px] text-[var(--c-ink-4)]">Hover to inspect</div>
          )}
        </div>

        {/* Hour labels */}
        <div className="grid grid-cols-[44px_repeat(48,1fr)] mb-1">
          <div />
          {Array.from({length: 48}).map((_, i) => (
            <div key={i} className="h-3 relative">
              {i % 4 === 0 && <span className="absolute -left-1.5 text-[8px] text-[var(--c-ink-4)] font-mono">{(i/2).toString().padStart(2,'0')}</span>}
            </div>
          ))}
        </div>

        {DAYS_DISPLAY_LABELS.map((day, displayIdx) => {
          const dayLocalMon0 = DISPLAY_TO_DATA[displayIdx];
          return (
            <div key={day} className="grid grid-cols-[44px_repeat(48,1fr)] h-5 gap-[1px] mb-[1px]">
              <div className="flex items-center justify-end pr-2 text-[9px] font-bold text-[var(--c-ink-4)]">{day}</div>
              {Array.from({length: 48}).map((_, slotIdx) => {
                const dataIdx = localCellToBitIdx(weekStartIso, dayLocalMon0, slotIdx, userTimezone);
                const count = overlap[dataIdx] || 0;
                const intensity = maxOverlap > 0 ? count / maxOverlap : 0;
                const hour = Math.floor(slotIdx / 2);
                const min = (slotIdx % 2) * 30;
                const isInChain = chainHighlight.has(dataIdx);
                const isHovered = hoveredSlot?.dataIdx === dataIdx;
                return (
                  <div
                    key={slotIdx}
                    onMouseEnter={() => handleSlotEnter(dataIdx, day, hour, min)}
                    onMouseLeave={() => { setHoveredSlot(null); setChainHighlight(new Set()); }}
                    className="rounded-[1px] transition-all duration-75"
                    style={{
                      backgroundColor: count === 0 
                        ? 'rgba(255,255,255,0.02)' 
                        : count === totalMembers
                          ? isInChain ? 'rgba(250, 204, 21, 0.85)' : `rgba(250, 204, 21, ${0.45 + intensity * 0.4})`
                          : isInChain
                            ? 'rgba(6, 182, 212, 0.85)'
                            : `rgba(6, 182, 212, ${0.1 + intensity * 0.7})`,
                      outline: isHovered ? '1px solid rgba(255,255,255,0.4)' : 'none',
                      zIndex: isHovered ? 10 : 'auto'
                    }}
                  />
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}
